import * as L from 'leaflet';
import { TurfHelper } from '../turf-helper';
import { PolygonInformationService } from '../polygon-information.service';
import { IconFactory } from '../icon-factory';
import { PolygonUtil } from '../polygon.util';
import { DrawMode, MarkerPosition } from '../enums';
import { Compass, PolyDrawUtil, Perimeter, Area } from '../utils';
import { leafletAdapter } from '../compatibility/leaflet-adapter';
import { LeafletVersionDetector } from '../compatibility/version-detector';
import { EventAdapter } from '../compatibility/event-adapter';
import type { Feature, Polygon, MultiPolygon, FeatureCollection, Point } from 'geojson';
import type {
  PolydrawConfig,
  PolydrawFeatureGroup,
  PolydrawPolygon,
  PolydrawEdgePolyline,
  PolygonUpdatedEventData,
  HistoryAction,
  ModifierKey,
  LayerInteraction,
  PolygonStyleOverrides,
  PolygonMenuAction,
  PolygonMenuActionContext,
} from '../types/polydraw-interfaces';
import { ModeManager } from './mode-manager';
import { EventManager } from './event-manager';
import { isTouchDevice } from './../utils';
import { isTestEnvironment } from '../utils';
import { PolygonTransformController } from '../transform/polygon-transform-controller';
import { PopupFactory } from '../popup-factory';
import type { LayerManager } from './layer-manager';

export interface InteractionResult {
  success: boolean;
  featureGroups?: L.FeatureGroup[];
  error?: string;
}

export interface PolygonInteractionManagerDependencies {
  turfHelper: TurfHelper;
  polygonInformation: PolygonInformationService;
  map: L.Map;
  config: PolydrawConfig;
  modeManager: ModeManager;
  eventManager: EventManager;
  saveHistoryState?: (action: HistoryAction) => void;
  layerManager?: LayerManager;
}

interface MarkerImportanceOptions {
  menuIndex: number;
  deleteIndex: number;
  infoIndex: number;
  optimizationLevel: number;
}

type DragDocumentListener = (event: MouseEvent | PointerEvent) => void;
type LeafletPolygonLatLngs = L.LatLng[] | L.LatLng[][] | L.LatLng[][][];
type BuiltInPolygonMenuAction = 'simplify' | 'doubleElbows' | 'bbox' | 'bezier';
type PolygonTransformMode = 'scale' | 'rotate' | 'donut';
type PolydrawAuxiliaryPolyline = L.Polyline & { _polydrawAuxiliaryLayer?: true };
type PolygonNativeStartListeners = {
  mousedown: (event: MouseEvent) => void;
  pointerdown: (event: PointerEvent) => void;
  touchstart: (event: TouchEvent) => void;
};

interface BuiltInMenuButtonConfig {
  enabled: boolean;
  id: string;
  label: string;
  classNames: string[];
}

const BUILT_IN_POLYGON_MENU_ACTION_IDS = new Set([
  'simplify',
  'doubleElbows',
  'bbox',
  'bezier',
  'scale',
  'rotate',
  'donut',
  'toggleOptimization',
]);

/**
 * PolygonInteractionManager handles all interactions with existing polygons.
 * This includes dragging polygons, dragging markers, edge interactions, and popup menus.
 */
export class PolygonInteractionManager {
  private markerFeatureGroupMap = new WeakMap<L.Marker, L.FeatureGroup>();
  private polygonFeatureGroupMap = new WeakMap<L.Polygon, L.FeatureGroup>();
  private markerModifierHandlers = new WeakMap<L.Marker, (e: Event) => void>();
  private markerTouchListeners = new WeakMap<
    L.Marker,
    { touchstart: (e: Event) => void; touchend: (e: Event) => void }
  >();
  private markerEdgeDeletionListeners = new WeakMap<
    L.Marker,
    { mouseenter: (e: Event) => void; mouseleave: (e: Event) => void }
  >();
  private _activeMarker: L.Marker | null = null;
  private isDraggingMarker = false;
  private turfHelper: TurfHelper;
  private polygonInformation: PolygonInformationService;
  private map: L.Map;
  private config: PolydrawConfig;
  private modeManager: ModeManager;
  private eventManager: EventManager;
  private saveHistoryState?: (action: HistoryAction) => void;
  private layerManager?: LayerManager;

  // Polygon drag state
  private currentDragPolygon: PolydrawPolygon | null = null;
  private currentDragIsClone: boolean = false;
  private currentModifierDragMode: boolean = false;
  private isModifierKeyHeld: boolean = false;
  private currentCloneGhost: L.Polygon | null = null;
  private dragCancelHandlersAttached: boolean = false;
  private _boundDragTouchMove: ((e: TouchEvent) => void) | null = null;
  private _boundDragTouchEnd: ((e: TouchEvent) => void) | null = null;
  private _boundDragMoveListener: DragDocumentListener | null = null;
  private _boundDragEndListener: DragDocumentListener | null = null;
  private dragUsesPointerEvents: boolean = false;
  private polygonTouchStartListeners = new WeakMap<L.Polygon, (e: TouchEvent) => void>();
  private polygonNativeStartListeners = new WeakMap<L.Polygon, PolygonNativeStartListeners>();
  private _openMenuPopup: L.Popup | null = null;
  private transformModeActive: boolean = false;
  private transformControllers = new WeakMap<L.FeatureGroup, PolygonTransformController>();
  private transformAuxiliaryDisplayValues = new WeakMap<L.Polyline, string>();
  private deleteMarkerSuppressUntil = 0;
  private readonly onModeChange = () => {
    if (this.currentCloneGhost || this.isPolygonDragActive()) {
      this.cancelActivePolygonDrag();
    }
  };
  /**
   * Strongly typed helper to emit polygon updated events without casts.
   * Also saves history state before emitting (except for drag operations which save on dragstart).
   */
  private emitPolygonUpdated(data: PolygonUpdatedEventData): void {
    // Save history state before modification for all operations except drag operations
    // (markerDrag and polygonDrag already save state on dragstart)
    const historyAction = this.getHistoryActionForOperation(data.operation);
    if (historyAction && this.saveHistoryState) {
      this.saveHistoryState(historyAction);
    }
    this.eventManager.emit('polydraw:polygon:updated', data);
  }

  private getHistoryActionForOperation(operation: string): HistoryAction | null {
    switch (operation) {
      case 'addVertex':
      case 'removeVertex':
      case 'toggleOptimization':
        return operation;
      case 'modifierSubtract':
      case 'modifierSubtractFallback':
        return 'modifierSubtract';
      default:
        return null;
    }
  }

  /**
   * True when the polygon in the feature group already has holes, or is a MultiPolygon
   * with more than one outer ring (donut mode only supports simple single-ring polygons).
   */
  private donutTargetHasHoles(featureGroup: L.FeatureGroup): boolean {
    const polygonLayer = featureGroup.getLayers().find((l) => l instanceof L.Polygon) as
      | L.Polygon
      | undefined;
    if (!polygonLayer) return true;
    const latLngs = polygonLayer.getLatLngs() as unknown as LeafletPolygonLatLngs;
    if (!Array.isArray(latLngs) || latLngs.length === 0) return true;
    // LatLng[] -> simple ring, no holes
    if (!Array.isArray(latLngs[0])) return false;
    const firstLevel = latLngs as L.LatLng[][] | L.LatLng[][][];
    // LatLng[][] -> Polygon; holes when there's more than one ring
    if (!Array.isArray((firstLevel[0] as unknown[])[0])) {
      return (firstLevel as L.LatLng[][]).length > 1;
    }
    // LatLng[][][] -> MultiPolygon; treat as unsupported (hidden)
    return true;
  }

  /**
   * Commit handler for donut mode. Decides inward vs outward via containment, assembles a
   * polygon-with-hole, and emits the updated event. On partial overlap / degenerate scale
   * the polygon is restored unchanged.
   */
  private handleDonutCommit(
    controller: PolygonTransformController,
    featureGroup: L.FeatureGroup,
    polygonLayer: L.Polygon,
    optimizationLevel: number,
    originalOptimizationLevel: number,
  ): void {
    const originalOuter = controller.getOriginalOuterLatLngs();
    const donutValidation = controller.getDonutValidation();

    const restore = () => {
      polygonLayer.setLatLngs([originalOuter] as unknown as L.LatLngExpression[]);
      this.setMarkerVisibility(polygonLayer as unknown as PolydrawPolygon, true);
    };

    if (!donutValidation.submitEnabled || !donutValidation.polygon) {
      restore();
      return;
    }

    // Restore the polygon's layer geometry to the pre-donut shape so the history snapshot
    // captures the state we're mutating away from (not the live dual-ring preview).
    polygonLayer.setLatLngs([originalOuter] as unknown as L.LatLngExpression[]);

    if (this.saveHistoryState) {
      this.saveHistoryState('donut');
    }

    this.cleanupFeatureGroup(featureGroup);
    this.removeFeatureGroup(featureGroup);
    this.emitPolygonUpdated({
      operation: 'donut',
      polygon: donutValidation.polygon,
      allowMerge: donutValidation.allowMerge,
      optimizationLevel,
      originalOptimizationLevel,
    });
  }

  // Read-only access to feature groups
  private getFeatureGroups: () => L.FeatureGroup[];
  private removeFeatureGroup: (fg: L.FeatureGroup) => void;

  constructor(
    dependencies: PolygonInteractionManagerDependencies,
    featureGroupAccess: {
      getFeatureGroups: () => L.FeatureGroup[];
      addFeatureGroup: (fg: L.FeatureGroup) => void;
      removeFeatureGroup: (fg: L.FeatureGroup) => void;
    },
  ) {
    this.turfHelper = dependencies.turfHelper;
    this.polygonInformation = dependencies.polygonInformation;
    this.map = dependencies.map;
    this.config = dependencies.config;
    this.modeManager = dependencies.modeManager;
    this.eventManager = dependencies.eventManager;
    this.saveHistoryState = dependencies.saveHistoryState;
    this.layerManager = dependencies.layerManager;

    // Store feature group access methods
    this.getFeatureGroups = featureGroupAccess.getFeatureGroups;
    this.removeFeatureGroup = featureGroupAccess.removeFeatureGroup;

    this.eventManager.on('polydraw:mode:change', this.onModeChange);
  }

  dispose(): void {
    this.eventManager.off('polydraw:mode:change', this.onModeChange);
    this.cancelActivePolygonDrag();
  }

  private getTimestamp(): number {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      return performance.now();
    }
    return Date.now();
  }

  private shouldSuppressDeleteMarkerClick(): boolean {
    return this.getTimestamp() < this.deleteMarkerSuppressUntil;
  }

  public suppressDeleteMarkerClicks(durationMs: number): void {
    this.deleteMarkerSuppressUntil = this.getTimestamp() + Math.max(0, durationMs);
  }

  /**
   * Add markers to a polygon feature group
   */
  addMarkers(
    latlngs: L.LatLngLiteral[],
    featureGroup: L.FeatureGroup,
    options: { optimizationLevel?: number; originalOptimizationLevel?: number } = {},
  ): void {
    this.registerPolygonLayerMappings(featureGroup);

    // Get initial marker positions
    let menuMarkerIdx = this.getMarkerIndex(latlngs, this.config.markers.markerMenuIcon.position);
    let deleteMarkerIdx = this.getMarkerIndex(
      latlngs,
      this.config.markers.markerDeleteIcon.position,
    );
    let infoMarkerIdx = this.getMarkerIndex(latlngs, this.config.markers.markerInfoIcon.position);

    // Apply fallback separation logic to ensure markers don't overlap
    const separatedIndices = this.ensureMarkerSeparation(latlngs.length, {
      menu: { index: menuMarkerIdx, enabled: this.config.markers.menuMarker },
      delete: { index: deleteMarkerIdx, enabled: this.config.markers.deleteMarker },
      info: { index: infoMarkerIdx, enabled: this.config.markers.infoMarker },
    });

    // Update indices with separated values
    menuMarkerIdx = separatedIndices.menu;
    deleteMarkerIdx = separatedIndices.delete;
    infoMarkerIdx = separatedIndices.info;

    const optimizationLevel = options.optimizationLevel ?? 0;
    const importantIndices = this.deriveImportantMarkerIndices(latlngs, {
      menuIndex: menuMarkerIdx,
      deleteIndex: deleteMarkerIdx,
      infoIndex: infoMarkerIdx,
      optimizationLevel,
    });
    const hasClosingPoint =
      latlngs.length > 2 && this.latLngEquals(latlngs[0], latlngs[latlngs.length - 1]);

    latlngs.forEach((latlng, i) => {
      let iconClasses = this.config.markers.markerIcon.styleClasses;
      if (i === menuMarkerIdx && this.config.markers.menuMarker) {
        iconClasses = this.config.markers.markerMenuIcon.styleClasses;
      }
      if (i === deleteMarkerIdx && this.config.markers.deleteMarker) {
        iconClasses = this.config.markers.markerDeleteIcon.styleClasses;
      }
      if (i === infoMarkerIdx && this.config.markers.infoMarker) {
        iconClasses = this.config.markers.markerInfoIcon.styleClasses;
      }

      const processedClasses = Array.isArray(iconClasses) ? iconClasses : [iconClasses];
      const isSpecialMarker =
        (i === menuMarkerIdx && this.config.markers.menuMarker) ||
        (i === deleteMarkerIdx && this.config.markers.deleteMarker) ||
        (i === infoMarkerIdx && this.config.markers.infoMarker);
      const normalizedIndex = this.normalizeMarkerIndex(i, latlngs.length, hasClosingPoint);
      const isImportant =
        optimizationLevel <= 0 ||
        normalizedIndex === null ||
        importantIndices.has(normalizedIndex) ||
        isSpecialMarker;
      const classesForIcon = [...processedClasses];
      if (!isImportant) {
        classesForIcon.push('polygon-marker-faded');
      }
      const marker = new L.Marker(latlng, {
        icon: this.createDivIcon(classesForIcon),
        draggable: this.config.modes.dragElbow,
        title: this.config.markers.coordsTitle ? this.getLatLngInfoString(latlng) : '',
        zIndexOffset:
          this.config.markers.markerIcon.zIndexOffset ?? this.config.markers.zIndexOffset,
      });

      featureGroup.addLayer(marker);

      // Attach reference to featureGroup for dragend/touchend logic
      this.markerFeatureGroupMap.set(marker, featureGroup);

      marker.on('add', () => {
        const el = marker.getElement();
        if (el) el.style.pointerEvents = 'auto';
      });

      marker.on('click', (e) => {
        if (this.isDraggingMarker) {
          e.originalEvent?.stopPropagation?.();
          L.DomEvent.stopPropagation(e);
        }
      });
      // Replace dragend binding to use _polydrawFeatureGroup and allow correct logic
      marker.on('dragstart', () => {
        // Layer guard: skip if not in active layer
        const fg = this.markerFeatureGroupMap.get(marker);
        if (fg && !this.isFeatureGroupInActiveLayer(fg)) {
          // Force-readonly for inactive layers if a stale draggable state slipped through.
          if (marker.dragging) {
            marker.dragging.disable();
          }
          return;
        }
        // Save history state before dragging
        if (this.saveHistoryState) {
          this.saveHistoryState('markerDrag');
        }
        this.isDraggingMarker = true;
        this._activeMarker = marker;
      });
      marker.on('dragend', (e: L.LeafletEvent) => {
        const fg = this.markerFeatureGroupMap.get(marker);
        // Layer guard
        if (fg && !this.isFeatureGroupInActiveLayer(fg)) {
          this._activeMarker = null;
          this.isDraggingMarker = false;
          return;
        }
        if (this.modeManager.canPerformAction('markerDrag') && fg) {
          this.markerDragEnd(fg);
        }
        this._activeMarker = null;
        L.DomEvent.stopPropagation(e);
        setTimeout(() => {
          this.isDraggingMarker = false;
        }, 10);
      });
      // Patch: Add touchend event for menu/info markers to simulate click on iOS
      const el = marker.getElement();
      if (el) {
        const touchstartHandler = (e: Event) => {
          e.stopPropagation();
        };
        const touchendHandler = (e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          marker.fire('click');
          // Also fire drag end logic for touch devices
          if (this.isDraggingMarker && !isSpecialMarker) {
            const fg = this.markerFeatureGroupMap.get(marker);
            if (
              this.modeManager.canPerformAction('markerDrag') &&
              fg &&
              this.isFeatureGroupInActiveLayer(fg)
            ) {
              this.markerDragEnd(fg);
            }
          }
          this._activeMarker = null;
        };

        el.addEventListener('touchstart', touchstartHandler, { passive: true });
        el.addEventListener('touchend', touchendHandler);

        // Store handlers for cleanup
        this.markerTouchListeners.set(marker, {
          touchstart: touchstartHandler,
          touchend: touchendHandler,
        });
      }

      // Set high z-index for special markers to ensure they're always visible on top
      if (i === menuMarkerIdx || i === deleteMarkerIdx || i === infoMarkerIdx) {
        const element = marker.getElement();
        if (element) {
          element.style.zIndex = '10000';
        }
      }

      if (this.config.modes.dragElbow) {
        const createDragHandler = (fg: L.FeatureGroup) => () => {
          if (
            this.modeManager.canPerformAction('markerDrag') &&
            this.isFeatureGroupInActiveLayer(fg)
          ) {
            this.markerDrag(fg);
          }
        };
        marker.on('drag', createDragHandler(featureGroup));
        // dragend is already handled above with the _polydrawFeatureGroup logic
      }

      if (i === menuMarkerIdx && this.config.markers.menuMarker) {
        marker.options.zIndexOffset =
          this.config.markers.markerMenuIcon.zIndexOffset ?? this.config.markers.zIndexOffset;
        marker.on('click', () => {
          // Layer guard
          if (!this.isFeatureGroupInActiveLayer(featureGroup)) return;
          const polygonGeoJSON = this.getPolygonGeoJSONFromFeatureGroup(featureGroup);
          const centerOfMass = PolygonUtil.getCenterOfPolygonByIndexWithOffsetFromCenterOfMass(
            polygonGeoJSON,
            menuMarkerIdx,
          );
          const menuPopup = this.generateMenuMarkerPopup(latlngs, featureGroup);
          menuPopup.setLatLng(centerOfMass).openOn(this.map);
        });
        // Patch: Adjust touchAction for map container on popup open/close
        marker.on('popupopen', (e) => {
          const popup = e.popup;
          const popupContent = popup.getElement();
          if (!popupContent) return;

          // Use a timeout to allow the popup to be fully rendered and positioned
          setTimeout(() => {
            const mapContainer = this.map.getContainer();
            const popupBounds = popupContent.getBoundingClientRect();

            // Re-measure bounds after repositioning and check if popup is out of bounds
            const mapBounds = mapContainer.getBoundingClientRect();
            let adjustX = 0;
            let adjustY = 0;

            if (popupBounds.left < mapBounds.left) {
              adjustX = mapBounds.left - popupBounds.left;
            } else if (popupBounds.right > mapBounds.right) {
              adjustX = mapBounds.right - popupBounds.right;
            }

            if (popupBounds.top < mapBounds.top) {
              adjustY = mapBounds.top - popupBounds.top;
            } else if (popupBounds.bottom > mapBounds.bottom) {
              adjustY = mapBounds.bottom - popupBounds.bottom;
            }

            if (adjustX !== 0 || adjustY !== 0) {
              popupContent.style.transform = `translate(${adjustX}px, ${adjustY}px)`;
            }
          }, 0);

          const container = this.map.getContainer();
          if (container) {
            container.style.touchAction = 'manipulation';
          }
        });
        marker.on('popupclose', () => {
          const container = this.map.getContainer();
          if (container) {
            container.style.touchAction = '';
          }
        });
      }
      if (i === infoMarkerIdx && this.config.markers.infoMarker) {
        // Get the complete polygon GeoJSON to properly handle holes
        const polygonGeoJSON = this.getPolygonGeoJSONFromFeatureGroup(featureGroup);
        const area = this.turfHelper.getPolygonArea(polygonGeoJSON);
        const perimeter = this.getTotalPolygonPerimeter(polygonGeoJSON);
        marker.options.zIndexOffset =
          this.config.markers.markerInfoIcon.zIndexOffset ?? this.config.markers.zIndexOffset;
        marker.on('click', () => {
          // Layer guard: only allow info popup for active layer
          if (!this.isFeatureGroupInActiveLayer(featureGroup)) return;
          const infoPopup = this.generateInfoMarkerPopup(area, perimeter);
          const centerOfMass = PolygonUtil.getCenterOfPolygonByIndexWithOffsetFromCenterOfMass(
            polygonGeoJSON,
            infoMarkerIdx,
          );
          infoPopup.setLatLng(centerOfMass).openOn(this.map);
        });
        // Patch: Adjust touchAction for map container on popup open/close
        marker.on('popupopen', (e) => {
          const popup = e.popup;
          const popupContent = popup.getElement();
          if (!popupContent) return;

          // Use a timeout to allow the popup to be fully rendered and positioned
          setTimeout(() => {
            const mapContainer = this.map.getContainer();
            const mapBounds = mapContainer.getBoundingClientRect();
            const popupBounds = popupContent.getBoundingClientRect();

            // Check if popup is out of bounds and adjust
            if (popupBounds.left < mapBounds.left) {
              popupContent.style.transform = `translateX(${mapBounds.left - popupBounds.left}px)`;
            } else if (popupBounds.right > mapBounds.right) {
              popupContent.style.transform = `translateX(${mapBounds.right - popupBounds.right}px)`;
            }
          }, 0);

          const container = this.map.getContainer();
          if (container) {
            container.style.touchAction = 'manipulation';
          }
        });
        marker.on('popupclose', () => {
          const container = this.map.getContainer();
          if (container) {
            container.style.touchAction = '';
          }
        });
      }

      // Forward mousedown events to the map when in drawing mode
      marker.on('mousedown', (e) => {
        if (!this.modeManager.isInOffMode()) {
          L.DomEvent.stopPropagation(e);
          this.map.fire('mousedown', e);
        }
        this._activeMarker = marker;
      });

      // Generic click handler for all markers
      marker.on('click', (e) => {
        // Layer guard: only allow interactions for active layer
        if (!this.isFeatureGroupInActiveLayer(featureGroup)) {
          return;
        }
        if (this.modeManager.isInOffMode()) {
          if (this.isEdgeDeletionModifierKeyPressed(e.originalEvent)) {
            const polygonLayer = featureGroup
              .getLayers()
              .find((layer) => layer instanceof L.Polygon) as L.Polygon | undefined;
            if (polygonLayer) {
              this.elbowClicked(e, polygonLayer, marker.getLatLng());
            }
          } else {
            // Handle non-modifier clicks for special markers
            if (i === deleteMarkerIdx && this.config.markers.deleteMarker) {
              if (this.shouldSuppressDeleteMarkerClick()) {
                const original = e.originalEvent as Event | undefined;
                original?.preventDefault?.();
                original?.stopPropagation?.();
                return;
              }
              this.map.closePopup();
              // Save state before deleting polygon
              if (this.saveHistoryState) {
                this.saveHistoryState('deletePolygon');
              }
              this.cleanupFeatureGroup(featureGroup);
              this.removeFeatureGroup(featureGroup);
              this.polygonInformation.createPolygonInformationStorage(this.getFeatureGroups());
              this.eventManager.emit('polydraw:polygon:deleted', undefined);
            }
          }
        }
      });

      // Add hover listeners for edge deletion feedback (guarded by active layer)
      marker.on('mouseover', () => {
        if (!this.isFeatureGroupInActiveLayer(featureGroup)) return;
        this.onMarkerHoverForEdgeDeletion(marker, true);
      });
      marker.on('mouseout', () => {
        this.onMarkerHoverForEdgeDeletion(marker, false);
      });
    });

    // Global fix for iOS: allow interaction with popups and markers
    const container = this.map.getContainer();
    if (container) {
      container.style.touchAction = 'manipulation';
    }
  }

  /**
   * Add hole markers to a polygon feature group, with configurable special markers.
   */
  addHoleMarkers(latlngs: L.LatLngLiteral[], featureGroup: L.FeatureGroup): void {
    this.registerPolygonLayerMappings(featureGroup);

    // Determine if special markers for holes are enabled
    const holeMenuEnabled = this.config.markers.holeMarkers?.menuMarker ?? false;
    const holeDeleteEnabled = this.config.markers.holeMarkers?.deleteMarker ?? false;
    const holeInfoEnabled = this.config.markers.holeMarkers?.infoMarker ?? false;

    // Get initial marker positions
    let menuMarkerIdx = this.getMarkerIndex(latlngs, this.config.markers.markerMenuIcon.position);
    let deleteMarkerIdx = this.getMarkerIndex(
      latlngs,
      this.config.markers.markerDeleteIcon.position,
    );
    let infoMarkerIdx = this.getMarkerIndex(latlngs, this.config.markers.markerInfoIcon.position);

    // Apply fallback separation logic to ensure markers don't overlap
    const separatedIndices = this.ensureMarkerSeparation(latlngs.length, {
      menu: { index: menuMarkerIdx, enabled: holeMenuEnabled },
      delete: { index: deleteMarkerIdx, enabled: holeDeleteEnabled },
      info: { index: infoMarkerIdx, enabled: holeInfoEnabled },
    });

    // Update indices with separated values
    menuMarkerIdx = separatedIndices.menu;
    deleteMarkerIdx = separatedIndices.delete;
    infoMarkerIdx = separatedIndices.info;

    latlngs.forEach((latlng, i) => {
      let iconClasses = this.config.markers.holeIcon.styleClasses;
      if (i === menuMarkerIdx && holeMenuEnabled) {
        iconClasses = this.config.markers.markerMenuIcon.styleClasses;
      }
      if (i === deleteMarkerIdx && holeDeleteEnabled) {
        iconClasses = this.config.markers.markerDeleteIcon.styleClasses;
      }
      if (i === infoMarkerIdx && holeInfoEnabled) {
        iconClasses = this.config.markers.markerInfoIcon.styleClasses;
      }

      const processedClasses = Array.isArray(iconClasses) ? iconClasses : [iconClasses];
      const isSpecialMarker =
        (i === menuMarkerIdx && holeMenuEnabled) ||
        (i === deleteMarkerIdx && holeDeleteEnabled) ||
        (i === infoMarkerIdx && holeInfoEnabled);

      const marker = new L.Marker(latlng, {
        icon: this.createDivIcon(processedClasses),
        draggable: this.config.modes.dragElbow,
        title: this.config.markers.coordsTitle ? this.getLatLngInfoString(latlng) : '',
        zIndexOffset: this.config.markers.holeIcon.zIndexOffset ?? this.config.markers.zIndexOffset,
      });

      featureGroup.addLayer(marker);

      // Attach reference to featureGroup for dragend/touchend logic
      this.markerFeatureGroupMap.set(marker, featureGroup);

      marker.on('add', () => {
        const el = marker.getElement();
        if (el) el.style.pointerEvents = 'auto';
      });

      marker.on('click', (e) => {
        if (this.isDraggingMarker) {
          e.originalEvent?.stopPropagation?.();
          L.DomEvent.stopPropagation(e);
        }
      });

      marker.on('dragstart', () => {
        const fg = this.markerFeatureGroupMap.get(marker);
        if (fg && !this.isFeatureGroupInActiveLayer(fg)) {
          if (marker.dragging) {
            marker.dragging.disable();
          }
          return;
        }
        if (this.saveHistoryState) {
          this.saveHistoryState('markerDrag');
        }
        this.isDraggingMarker = true;
        this._activeMarker = marker;
      });

      marker.on('dragend', (e: L.LeafletEvent) => {
        const fg = this.markerFeatureGroupMap.get(marker);
        if (fg && !this.isFeatureGroupInActiveLayer(fg)) {
          this._activeMarker = null;
          this.isDraggingMarker = false;
          return;
        }
        if (this.modeManager.canPerformAction('markerDrag') && fg) {
          this.markerDragEnd(fg);
        }
        this._activeMarker = null;
        L.DomEvent.stopPropagation(e);
        setTimeout(() => {
          this.isDraggingMarker = false;
        }, 10);
      });

      const el = marker.getElement();
      if (el) {
        const touchstartHandler = (e: Event) => {
          e.stopPropagation();
        };
        const touchendHandler = (e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          marker.fire('click');
          if (this.isDraggingMarker && !isSpecialMarker) {
            const fg = this.markerFeatureGroupMap.get(marker);
            if (
              this.modeManager.canPerformAction('markerDrag') &&
              fg &&
              this.isFeatureGroupInActiveLayer(fg)
            ) {
              this.markerDragEnd(fg);
            }
          }
          this._activeMarker = null;
        };

        el.addEventListener('touchstart', touchstartHandler, { passive: true });
        el.addEventListener('touchend', touchendHandler);

        // Store handlers for cleanup
        this.markerTouchListeners.set(marker, {
          touchstart: touchstartHandler,
          touchend: touchendHandler,
        });
      }

      if (i === menuMarkerIdx || i === deleteMarkerIdx || i === infoMarkerIdx) {
        const element = marker.getElement();
        if (element) {
          element.style.zIndex = '10000';
        }
      }

      if (this.config.modes.dragElbow) {
        const createDragHandler = (fg: L.FeatureGroup) => () => {
          if (
            this.modeManager.canPerformAction('markerDrag') &&
            this.isFeatureGroupInActiveLayer(fg)
          ) {
            this.markerDrag(fg);
          }
        };
        marker.on('drag', createDragHandler(featureGroup));
      }

      // --- Special Marker Logic for Holes ---

      if (i === menuMarkerIdx && holeMenuEnabled) {
        marker.options.zIndexOffset =
          this.config.markers.markerMenuIcon.zIndexOffset ?? this.config.markers.zIndexOffset;

        marker.on('click', () => {
          // Build a minimal GeoJSON for this hole ring to compute center-of-mass
          const ring = latlngs.map((pt) => [pt.lng, pt.lat]);
          ring.push(ring[0]); // close ring

          const holePolygon: Feature<Polygon> = {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'Polygon',
              coordinates: [ring],
            },
          };

          const centerOfMass = PolygonUtil.getCenterOfMass(holePolygon);
          const menuPopup = this.generateMenuMarkerPopup(latlngs, featureGroup);

          menuPopup.setLatLng(centerOfMass).openOn(this.map);
        });

        // Keep popup positioning/touch behaviour consistent with outer ring menu
        marker.on('popupopen', (e) => {
          const popup = e.popup;
          const popupContent = popup.getElement();
          if (!popupContent) return;
          setTimeout(() => {
            const mapContainer = this.map.getContainer();
            const mapBounds = mapContainer.getBoundingClientRect();
            const popupBounds = popupContent.getBoundingClientRect();
            if (popupBounds.left < mapBounds.left) {
              popupContent.style.transform = `translateX(${mapBounds.left - popupBounds.left}px)`;
            } else if (popupBounds.right > mapBounds.right) {
              popupContent.style.transform = `translateX(${mapBounds.right - popupBounds.right}px)`;
            }
          }, 0);
          const container = this.map.getContainer();
          if (container) {
            container.style.touchAction = 'manipulation';
          }
        });
        marker.on('popupclose', () => {
          const container = this.map.getContainer();
          if (container) {
            container.style.touchAction = '';
          }
        });
      }

      if (i === infoMarkerIdx && holeInfoEnabled) {
        const ring = latlngs.map((latlng) => [latlng.lng, latlng.lat]);
        ring.push(ring[0]); // Close the ring

        const holePolygon: Feature<Polygon> = {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [ring],
          },
        };
        const area = this.turfHelper.getPolygonArea(holePolygon);
        const perimeter = this.turfHelper.getPolygonPerimeter(holePolygon) * 1000; // to meters

        marker.options.zIndexOffset =
          this.config.markers.markerInfoIcon.zIndexOffset ?? this.config.markers.zIndexOffset;
        marker.on('click', () => {
          // Layer guard: only allow info popup for active layer
          if (!this.isFeatureGroupInActiveLayer(featureGroup)) return;
          const infoPopup = this.generateInfoMarkerPopup(area, perimeter);
          infoPopup.setLatLng(latlng).openOn(this.map);
        });
      }

      marker.on('mousedown', (e) => {
        if (!this.modeManager.isInOffMode()) {
          L.DomEvent.stopPropagation(e);
          this.map.fire('mousedown', e);
        }
        this._activeMarker = marker;
      });

      marker.on('click', (e) => {
        // Layer guard: only allow interactions for active layer
        if (!this.isFeatureGroupInActiveLayer(featureGroup)) return;
        if (this.modeManager.isInOffMode()) {
          if (this.isEdgeDeletionModifierKeyPressed(e.originalEvent)) {
            const polygonLayer = featureGroup
              .getLayers()
              .find((layer) => layer instanceof L.Polygon) as L.Polygon | undefined;
            if (polygonLayer) {
              this.elbowClicked(e, polygonLayer, marker.getLatLng());
            }
          } else {
            if (i === deleteMarkerIdx && holeDeleteEnabled) {
              this.map.closePopup();
              // Delete the entire hole by matching against Leaflet lat/lngs
              const parentPolygon = featureGroup
                .getLayers()
                .find((layer) => layer instanceof L.Polygon) as L.Polygon;
              if (parentPolygon) {
                // Normalize Leaflet polygon latlngs to an array of rings: L.LatLng[][]
                const rawLatLngs = parentPolygon.getLatLngs();
                let rings: L.LatLng[][] = [];
                if (Array.isArray(rawLatLngs) && rawLatLngs.length > 0) {
                  if (Array.isArray(rawLatLngs[0])) {
                    const first = (rawLatLngs as unknown[])[0];
                    if (Array.isArray(first) && Array.isArray((first as unknown[])[0])) {
                      // MultiPolygon-like structure: [ [ [LatLng] ] ] – flatten first level
                      rings = (
                        rawLatLngs as unknown as L.LatLng[][][]
                      )[0] as unknown as L.LatLng[][];
                    } else {
                      // Polygon with holes: [ [LatLng] , [LatLng] ... ]
                      rings = rawLatLngs as L.LatLng[][];
                    }
                  } else {
                    // Single ring provided as LatLng[]
                    rings = [rawLatLngs as unknown as L.LatLng[]];
                  }
                }

                // Identify which ring is the hole containing this delete marker
                const target = marker.getLatLng();
                let holeIndex = -1;
                for (let r = 1; r < rings.length; r++) {
                  // start at 1 to skip outer ring
                  if (rings[r].some((p) => p.lat === target.lat && p.lng === target.lng)) {
                    holeIndex = r;
                    break;
                  }
                }

                if (holeIndex > 0) {
                  // Build new rings without the hole
                  const newRings: L.LatLng[][] = rings.filter((_, idx) => idx !== holeIndex);

                  // Convert to GeoJSON coordinates and ensure closure of each ring
                  const coords = newRings.map((ring) => {
                    const arr = ring.map((ll) => [ll.lng, ll.lat]);
                    if (arr.length > 0) {
                      const firstPt = arr[0];
                      const lastPt = arr[arr.length - 1];
                      if (firstPt[0] !== lastPt[0] || firstPt[1] !== lastPt[1]) {
                        arr.push([firstPt[0], firstPt[1]]);
                      }
                    }
                    return arr;
                  });

                  const { level: optimizationLevel, original: originalOptimizationLevel } =
                    this.getOptimizationMetadataFromFeatureGroup(featureGroup);
                  const featureMetadataState = this.getFeatureMetadataState(featureGroup);
                  const newPolygon = this.turfHelper.getMultiPolygon([coords]);
                  if (this.saveHistoryState) {
                    this.saveHistoryState('removeHole');
                  }
                  this.cleanupFeatureGroup(featureGroup);
                  this.removeFeatureGroup(featureGroup);
                  this.emitPolygonUpdated({
                    operation: 'removeHole',
                    polygon: newPolygon,
                    optimizationLevel,
                    originalOptimizationLevel,
                    featureId: featureMetadataState.featureId,
                    featureMetadata: featureMetadataState.metadata,
                    sourceFeatureIds: featureMetadataState.sourceFeatureIds,
                    featureInteractionOverride: featureMetadataState.interactionOverride,
                    featureStyleOverrides: featureMetadataState.styleOverrides,
                    featureCreatedAt: featureMetadataState.createdAt,
                  });
                }
              }
            }
          }
        }
      });

      marker.on('mouseover', () => {
        if (!this.isFeatureGroupInActiveLayer(featureGroup)) return;
        this.onMarkerHoverForEdgeDeletion(marker, true);
      });
      marker.on('mouseout', () => {
        this.onMarkerHoverForEdgeDeletion(marker, false);
      });
    });
  }

  /**
   * Add edge click listeners to a polygon
   */
  addEdgeClickListeners(polygon: L.Polygon, featureGroup: L.FeatureGroup): void {
    const rawLatLngs = polygon.getLatLngs();

    // Handle different polygon structures
    let processedRings: L.LatLngLiteral[][];

    if (Array.isArray(rawLatLngs) && rawLatLngs.length > 0) {
      if (Array.isArray(rawLatLngs[0])) {
        if (Array.isArray(rawLatLngs[0][0]) && rawLatLngs[0][0].length > 0) {
          const firstCoord = rawLatLngs[0][0][0];
          if (firstCoord && typeof firstCoord === 'object' && 'lat' in firstCoord) {
            processedRings = rawLatLngs[0] as L.LatLngLiteral[][];
          } else {
            processedRings = rawLatLngs[0] as L.LatLngLiteral[][];
          }
        } else if (
          rawLatLngs[0][0] &&
          typeof rawLatLngs[0][0] === 'object' &&
          'lat' in rawLatLngs[0][0]
        ) {
          processedRings = rawLatLngs as L.LatLngLiteral[][];
        } else {
          processedRings = rawLatLngs[0] as L.LatLngLiteral[][];
        }
      } else if (rawLatLngs[0] && typeof rawLatLngs[0] === 'object' && 'lat' in rawLatLngs[0]) {
        processedRings = [rawLatLngs as L.LatLngLiteral[]];
      } else {
        processedRings = [rawLatLngs as L.LatLngLiteral[]];
      }
    } else {
      return;
    }

    processedRings.forEach((ring, ringIndex) => {
      for (let i = 0; i < ring.length; i++) {
        const edgeStart = ring[i];
        const edgeEnd = ring[(i + 1) % ring.length];

        if (edgeStart.lat === edgeEnd.lat && edgeStart.lng === edgeEnd.lng) {
          continue;
        }

        const edgePolyline = leafletAdapter.createPolyline(
          [edgeStart as L.LatLng, edgeEnd as L.LatLng],
          {
            color: 'transparent',
            weight: 10,
            opacity: 0,
            interactive: true,
          },
        );

        (edgePolyline as PolydrawAuxiliaryPolyline)._polydrawAuxiliaryLayer = true;
        (edgePolyline as PolydrawEdgePolyline)._polydrawEdgeInfo = {
          ringIndex,
          edgeIndex: i,
          startPoint: edgeStart,
          endPoint: edgeEnd,
          parentPolygon: polygon,
          parentFeatureGroup: featureGroup,
        };

        edgePolyline.on('click', (e: L.LeafletMouseEvent) => {
          this.onEdgeClick(e, edgePolyline);
        });

        edgePolyline.on('mouseover', () => {
          this.highlightEdgeOnHover(edgePolyline, true);
        });

        edgePolyline.on('mouseout', () => {
          this.highlightEdgeOnHover(edgePolyline, false);
        });

        featureGroup.addLayer(edgePolyline);
      }
    });
  }

  /**
   * Check whether polygon dragging is allowed for the current mode.
   */
  private isPolygonDragModeActive(): boolean {
    const mode = this.modeManager.getCurrentMode();
    return mode === DrawMode.Off || mode === DrawMode.Clone;
  }

  private isPolygonDragActive(): boolean {
    return !!(
      this.currentDragPolygon &&
      this.currentDragPolygon._polydrawDragData &&
      this.currentDragPolygon._polydrawDragData.isDragging
    );
  }

  /**
   * Check if a polygon belongs to the active layer.
   * Returns true if no layer manager exists (backward compat) or if in active editable layer.
   */
  private isPolygonInActiveLayer(polygon: L.Polygon): boolean {
    if (!this.layerManager) return true;
    const mappedFeatureGroup = this.polygonFeatureGroupMap.get(polygon);
    if (mappedFeatureGroup) {
      return (
        this.layerManager.isInActiveLayer(mappedFeatureGroup) &&
        this.layerManager.isFeatureGroupEditable(mappedFeatureGroup)
      );
    }

    const resolvedFeatureGroup = this.findFeatureGroupForPolygon(polygon);
    if (resolvedFeatureGroup) {
      this.polygonFeatureGroupMap.set(polygon, resolvedFeatureGroup);
      return (
        this.layerManager.isInActiveLayer(resolvedFeatureGroup) &&
        this.layerManager.isFeatureGroupEditable(resolvedFeatureGroup)
      );
    }
    return true; // If not found, allow interaction
  }

  /**
   * Check if a feature group belongs to the active layer.
   * Returns true if no layer manager exists (backward compat).
   */
  private isFeatureGroupInActiveLayer(fg: L.FeatureGroup): boolean {
    if (!this.layerManager) return true;
    return this.layerManager.isInActiveLayer(fg) && this.layerManager.isFeatureGroupEditable(fg);
  }

  private beginPolygonDrag(
    polygon: PolydrawPolygon,
    startLatLng: L.LatLng | null | undefined,
    originalEvent: Event,
    options: { attachTouchListeners?: boolean } = {},
  ): boolean {
    if (this.transformModeActive) return false;
    if (!this.isPolygonInActiveLayer(polygon)) return false;
    if (!this.isPolygonDragModeActive()) return false;
    if (!this.modeManager.canPerformAction('polygonDrag')) return false;
    if (!startLatLng) return false;

    originalEvent.stopPropagation?.();
    if (originalEvent.cancelable) {
      originalEvent.preventDefault?.();
    }

    const existingDragData = polygon._polydrawDragData;
    if (this.currentDragPolygon === polygon && existingDragData?.isDragging) {
      return true;
    }

    const isCloneMode = this.modeManager.getCurrentMode() === DrawMode.Clone;
    const isModifierPressed = isCloneMode
      ? false
      : this.detectDragSubtractModifierKey(originalEvent as MouseEvent | PointerEvent);
    this.currentDragIsClone = isCloneMode;
    this.currentModifierDragMode = isModifierPressed;
    this.isModifierKeyHeld = isModifierPressed;

    if (this.saveHistoryState) {
      this.saveHistoryState(isCloneMode ? 'polygonClone' : 'polygonDrag');
    }

    polygon._polydrawDragData = {
      ...(polygon._polydrawDragData ?? {
        originalOpacity: polygon.options.fillOpacity,
      }),
      isDragging: true,
      startPosition: startLatLng,
      startLatLngs: polygon.getLatLngs(),
    };

    polygon.setStyle({ fillOpacity: this.config.interaction.drag.opacity });
    if (isCloneMode) {
      this.showCloneGhost(polygon._polydrawDragData.startLatLngs as LeafletPolygonLatLngs);
    } else {
      this.clearCloneGhost();
    }

    if (this.map.dragging) {
      this.map.dragging.disable();
    }

    this.setSubtractVisualMode(polygon, isModifierPressed);
    this.setMarkerVisibility(polygon, false);

    try {
      const container = this.map.getContainer();
      container.style.cursor = this.config.interaction.drag.dragCursor || 'move';
    } catch {
      // Handle DOM errors
    }

    if (options.attachTouchListeners) {
      this.attachDragTouchListeners();
    }
    this.attachDragDocumentListeners();
    this.attachDragCancelHandlers();

    this.currentDragPolygon = polygon;
    return true;
  }

  private attachPolygonNativeStartListeners(polygon: PolydrawPolygon): void {
    if (this.polygonNativeStartListeners.has(polygon)) return;

    const startFromNativeEvent = (event: MouseEvent | PointerEvent | TouchEvent): void => {
      if (!this.isPolygonDragModeActive()) return;

      const latlng = EventAdapter.extractCoordinates(
        event as unknown as Record<string, unknown>,
        this.map,
      );
      this.beginPolygonDrag(polygon, latlng, event, {
        attachTouchListeners: event.type === 'touchstart',
      });
    };

    const listeners: PolygonNativeStartListeners = {
      mousedown: (event) => startFromNativeEvent(event),
      pointerdown: (event) => startFromNativeEvent(event),
      touchstart: (event) => startFromNativeEvent(event),
    };

    this.polygonNativeStartListeners.set(polygon, listeners);

    const attach = () => {
      const el = (polygon as unknown as { _path?: SVGElement })._path;
      if (!el) return;
      el.addEventListener('mousedown', listeners.mousedown, { capture: true });
      el.addEventListener('pointerdown', listeners.pointerdown, { capture: true });
      el.addEventListener('touchstart', listeners.touchstart, { capture: true, passive: false });
    };

    if ((polygon as unknown as { _path?: SVGElement })._path) {
      attach();
    } else {
      polygon.once('add', attach);
    }
  }

  /**
   * Enable polygon dragging functionality
   */
  enablePolygonDragging(polygon: PolydrawPolygon, latlngs: Feature<Polygon | MultiPolygon>): void {
    if (!this.config.modes.dragPolygons && !this.config.tools.clone) return;

    polygon._polydrawOriginalLatLngs = latlngs;
    polygon._polydrawDragData = {
      isDragging: false,
      startPosition: null,
      startLatLngs: null,
      originalOpacity: polygon.options.fillOpacity,
    };

    polygon.on('mousedown', (e: L.LeafletMouseEvent) => {
      if (!this.isPolygonDragModeActive()) {
        // Stop this event from becoming a drag, but fire it on the map for drawing.
        L.DomEvent.stopPropagation(e);
        this.map.fire('mousedown', e);
        return;
      }

      this.beginPolygonDrag(polygon, e.latlng, e.originalEvent);
    });

    // Support pointer events (Leaflet v2) in addition to mousedown
    (polygon as L.Evented).on('pointerdown', ((event: L.LeafletEvent) => {
      const e = event as L.LeafletMouseEvent;
      // If not in off mode, it's a drawing click. Forward to map and stop.
      if (!this.isPolygonDragModeActive()) {
        const originalEvent = (e.originalEvent ?? e) as Event;
        L.DomEvent.stopPropagation(originalEvent);
        (this.map as L.Evented).fire('pointerdown', e);
        return;
      }

      const orig = (e.originalEvent ?? e) as Event;
      this.beginPolygonDrag(polygon, e.latlng, orig);
    }) as L.LeafletEventHandlerFn);

    // Touch support for Leaflet v1 (v1 does not synthesize mouse/pointer events from touch on SVG)
    if (LeafletVersionDetector.isV1()) {
      const onTouchStart = (e: TouchEvent) => {
        if (!this.isPolygonDragModeActive()) {
          return;
        }

        const latlng = EventAdapter.extractCoordinates(
          e as unknown as Record<string, unknown>,
          this.map,
        );
        this.beginPolygonDrag(polygon, latlng, e, { attachTouchListeners: true });
      };

      this.polygonTouchStartListeners.set(polygon, onTouchStart);

      // Defer attachment until the SVG element exists on the map
      const attachTouch = () => {
        const el = (polygon as unknown as { _path?: SVGElement })._path;
        if (el) {
          el.addEventListener('touchstart', onTouchStart as unknown as (e: Event) => void, {
            passive: false,
          });
        }
      };

      // If already on the map, attach now; otherwise wait for 'add' event
      if ((polygon as unknown as { _path?: SVGElement })._path) {
        attachTouch();
      } else {
        polygon.once('add', attachTouch);
      }
    }

    this.attachPolygonNativeStartListeners(polygon);

    polygon.on('mouseover', () => {
      if (!this.isPolygonInActiveLayer(polygon)) return;
      if (!polygon._polydrawDragData || !polygon._polydrawDragData.isDragging) {
        try {
          const container = this.map.getContainer();
          container.style.cursor = this.config.interaction.drag.hoverCursor || 'grab';
        } catch {
          // Handle DOM errors
        }
      }
    });

    polygon.on('mouseout', () => {
      if (!polygon._polydrawDragData || !polygon._polydrawDragData.isDragging) {
        try {
          const container = this.map.getContainer();
          container.style.cursor = '';
        } catch {
          // Handle DOM errors
        }
      }
    });
  }

  /**
   * Update marker draggable state based on current mode
   */
  updateMarkerDraggableState(): void {
    const shouldBeDraggable = this.modeManager.canPerformAction('markerDrag');
    const markersShouldCapturePointer = this.modeManager.getCurrentMode() !== DrawMode.Clone;

    this.getFeatureGroups().forEach((featureGroup) => {
      // Layer guard: only allow drag for active layer markers
      const isActiveLayer = this.isFeatureGroupInActiveLayer(featureGroup);
      const effectiveDraggable = shouldBeDraggable && isActiveLayer;

      featureGroup.eachLayer((layer) => {
        if (layer instanceof L.Marker) {
          const marker = layer as L.Marker;
          try {
            // Update the draggable option
            marker.options.draggable = effectiveDraggable;
            const element = marker.getElement();
            if (element) {
              element.style.pointerEvents = markersShouldCapturePointer ? 'auto' : 'none';
            }

            // If the marker has dragging capability, update its state
            if (marker.dragging) {
              if (effectiveDraggable) {
                marker.dragging.enable();
              } else {
                marker.dragging.disable();
              }
            }
          } catch {
            // Handle any errors in updating marker state
          }
        }
      });
    });
  }

  /**
   * Update all markers for edge deletion visual feedback
   */
  updateAllMarkersForEdgeDeletion(showFeedback: boolean): void {
    this.getFeatureGroups().forEach((featureGroup) => {
      featureGroup.eachLayer((layer) => {
        if (layer instanceof L.Marker) {
          this.updateMarkerForEdgeDeletion(layer, showFeedback);
        }
      });
    });
  }

  /**
   * Update individual marker for edge deletion visual feedback
   */
  updateMarkerForEdgeDeletion(marker: L.Marker, showFeedback: boolean): void {
    const element = marker.getElement();
    if (!element) return;

    if (showFeedback) {
      // Add hover listeners for edge deletion feedback
      element.addEventListener('mouseenter', this.onMarkerHoverForEdgeDeletionEvent);
      element.addEventListener('mouseleave', this.onMarkerLeaveForEdgeDeletionEvent);

      // Store handlers for cleanup
      this.markerEdgeDeletionListeners.set(marker, {
        mouseenter: this.onMarkerHoverForEdgeDeletionEvent,
        mouseleave: this.onMarkerLeaveForEdgeDeletionEvent,
      });
    } else {
      // Remove hover listeners and reset style
      element.removeEventListener('mouseenter', this.onMarkerHoverForEdgeDeletionEvent);
      element.removeEventListener('mouseleave', this.onMarkerLeaveForEdgeDeletionEvent);
      element.style.backgroundColor = '';
      element.style.borderColor = '';

      // Clean up stored handlers
      this.markerEdgeDeletionListeners.delete(marker);
    }
  }

  /**
   * Set modifier key held state
   */
  setModifierKeyHeld(isHeld: boolean): void {
    this.isModifierKeyHeld = isHeld;
  }

  /**
   * Clean up all resources associated with a marker
   * This method ensures proper cleanup of event listeners and WeakMap entries
   */
  cleanupMarker(marker: L.Marker): void {
    const element = marker.getElement();

    // Always clean up document-level listeners first (DOM-optional)
    const modifierHandler = this.markerModifierHandlers.get(marker);
    if (modifierHandler) {
      document.removeEventListener('keydown', modifierHandler);
      document.removeEventListener('keyup', modifierHandler);
      if (element) {
        element.removeEventListener('mousemove', modifierHandler);
      }
      this.markerModifierHandlers.delete(marker);
    }

    // Clean up touch event listeners (remove from element if present, always delete mapping)
    const touchListeners = this.markerTouchListeners.get(marker);
    if (touchListeners) {
      if (element) {
        element.removeEventListener('touchstart', touchListeners.touchstart);
        element.removeEventListener('touchend', touchListeners.touchend);
      }
      this.markerTouchListeners.delete(marker);
    }

    // Clean up edge deletion event listeners (remove from element if present, always delete mapping)
    const edgeDeletionListeners = this.markerEdgeDeletionListeners.get(marker);
    if (edgeDeletionListeners) {
      if (element) {
        element.removeEventListener('mouseenter', edgeDeletionListeners.mouseenter);
        element.removeEventListener('mouseleave', edgeDeletionListeners.mouseleave);
      }
      this.markerEdgeDeletionListeners.delete(marker);
    }

    // Clean up feature group mapping last
    this.markerFeatureGroupMap.delete(marker);
  }

  /**
   * Clean up all resources associated with a feature group
   * This method ensures proper cleanup of all markers and associated resources
   */
  cleanupFeatureGroup(featureGroup: L.FeatureGroup): void {
    // Clean up all markers and polygon touch listeners in the feature group
    featureGroup.eachLayer((layer) => {
      if (layer instanceof L.Marker) {
        this.cleanupMarker(layer as L.Marker);
      } else if (layer instanceof L.Polygon) {
        this.detachPolygonNativeStartListeners(layer);
        this.detachPolygonTouchStart(layer);
        this.polygonFeatureGroupMap.delete(layer);
      }
    });

    this.destroyTransformController(featureGroup);
  }

  // Private methods

  private destroyTransformController(featureGroup: L.FeatureGroup): void {
    const transformController = this.transformControllers.get(featureGroup);
    if (!transformController) {
      return;
    }
    transformController.destroy();
    this.transformControllers.delete(featureGroup);
    this.transformModeActive = false;
  }

  private onEdgeClick(e: L.LeafletMouseEvent, edgePolyline: L.Polyline): void {
    if (!this.config.modes.attachElbow) {
      return;
    }
    if (!this.modeManager.isInOffMode()) {
      return;
    }
    const edgeInfo = (edgePolyline as PolydrawEdgePolyline)._polydrawEdgeInfo;
    if (!edgeInfo) return;
    // Layer guard: only allow edge interactions for active layer
    if (
      edgeInfo.parentFeatureGroup &&
      !this.isFeatureGroupInActiveLayer(edgeInfo.parentFeatureGroup)
    ) {
      return;
    }
    const newPoint = e.latlng;
    const parentPolygon = edgeInfo.parentPolygon;
    const parentFeatureGroup = edgeInfo.parentFeatureGroup;
    if (parentPolygon && parentFeatureGroup) {
      try {
        if (typeof parentPolygon.toGeoJSON !== 'function') {
          return;
        }
        const poly = parentPolygon.toGeoJSON();
        if (poly.geometry.type === 'MultiPolygon' || poly.geometry.type === 'Polygon') {
          const newPolygon = this.turfHelper.injectPointToPolygon(
            poly,
            [newPoint.lng, newPoint.lat],
            edgeInfo.ringIndex,
          );
          if (newPolygon) {
            const polydrawPolygon = parentPolygon as PolydrawPolygon;
            const optimizationLevel = polydrawPolygon._polydrawOptimizationLevel || 0;
            const originalOptimizationLevel =
              polydrawPolygon._polydrawOptimizationOriginalLevel || optimizationLevel;
            const featureMetadataState = this.getFeatureMetadataState(parentFeatureGroup);
            this.cleanupFeatureGroup(parentFeatureGroup);
            this.removeFeatureGroup(parentFeatureGroup);
            this.emitPolygonUpdated({
              operation: 'addVertex',
              polygon: newPolygon,
              optimizationLevel,
              originalOptimizationLevel,
              featureId: featureMetadataState.featureId,
              featureMetadata: featureMetadataState.metadata,
              sourceFeatureIds: featureMetadataState.sourceFeatureIds,
              featureInteractionOverride: featureMetadataState.interactionOverride,
              featureStyleOverrides: featureMetadataState.styleOverrides,
              featureCreatedAt: featureMetadataState.createdAt,
            });
          }
        }
      } catch {
        // Handle errors
      }
    }
    L.DomEvent.stopPropagation(e);
  }

  private highlightEdgeOnHover(edgePolyline: L.Polyline, isHovering: boolean): void {
    if (isHovering) {
      edgePolyline.setStyle({
        color: this.config.styles.ui.edgeHover.color,
        weight: 4,
        opacity: 1,
      });
    } else {
      edgePolyline.setStyle({
        color: 'transparent',
        weight: 10,
        opacity: 0,
      });
    }
  }

  private deriveImportantMarkerIndices(
    latlngs: L.LatLngLiteral[],
    options: MarkerImportanceOptions,
  ): Set<number> {
    const important = new Set<number>();
    if (!latlngs || latlngs.length === 0) {
      return important;
    }

    const normalizedLevel = Math.min(Math.max(options.optimizationLevel ?? 0, 0), 10);
    const hasClosingPoint =
      latlngs.length > 2 && this.latLngEquals(latlngs[0], latlngs[latlngs.length - 1]);
    const ring = hasClosingPoint ? latlngs.slice(0, -1) : [...latlngs];
    const vertexCount = ring.length;

    const addIndex = (idx?: number) => {
      if (idx === undefined || idx === null || idx < 0) {
        return;
      }
      let normalized = idx;
      if (hasClosingPoint && idx === latlngs.length - 1) {
        normalized = 0;
      }
      if (normalized >= vertexCount) {
        normalized = vertexCount > 0 ? normalized % vertexCount : 0;
      }
      if (normalized >= 0 && normalized < vertexCount) {
        important.add(normalized);
      }
    };

    addIndex(0);
    addIndex(options.menuIndex);
    addIndex(options.deleteIndex);
    addIndex(options.infoIndex);

    if (vertexCount === 0 || normalizedLevel <= 0 || vertexCount <= 3) {
      for (let i = 0; i < vertexCount; i++) {
        important.add(i);
      }
      return important;
    }

    const tolerance = this.getToleranceForOptimizationLevel(normalizedLevel);
    let simplifiedVertices =
      tolerance > 0 ? this.turfHelper.simplifyLatLngRing(ring, tolerance, true) : [...ring];

    if (
      simplifiedVertices.length > 1 &&
      this.latLngEquals(simplifiedVertices[0], simplifiedVertices[simplifiedVertices.length - 1])
    ) {
      simplifiedVertices = simplifiedVertices.slice(0, -1);
    }

    if (!simplifiedVertices || simplifiedVertices.length === 0) {
      for (let i = 0; i < vertexCount; i++) {
        important.add(i);
      }
      return important;
    }

    simplifiedVertices.forEach((vertex) => {
      const closestIndex = this.findClosestVertexIndex(vertex, ring);
      if (closestIndex !== null) {
        important.add(closestIndex);
      }
    });

    if (important.size === 0) {
      for (let i = 0; i < vertexCount; i++) {
        important.add(i);
      }
    }

    return important;
  }

  private normalizeMarkerIndex(
    index: number,
    length: number,
    hasClosingPoint: boolean,
  ): number | null {
    if (index === undefined || index === null || index < 0) {
      return null;
    }
    if (hasClosingPoint) {
      const uniqueLength = Math.max(1, length - 1);
      if (index === length - 1) {
        return 0;
      }
      if (index >= uniqueLength) {
        return index % uniqueLength;
      }
      return index;
    }
    if (index >= length) {
      return length > 0 ? length - 1 : null;
    }
    return index;
  }

  private latLngEquals(a: L.LatLngLiteral, b: L.LatLngLiteral): boolean {
    if (!a || !b) return false;
    return Math.abs(a.lat - b.lat) < 1e-9 && Math.abs(a.lng - b.lng) < 1e-9;
  }

  private isLatLngLiteral(value: unknown): value is L.LatLngLiteral {
    return (
      !!value &&
      typeof value === 'object' &&
      'lat' in value &&
      'lng' in value &&
      typeof (value as L.LatLngLiteral).lat === 'number' &&
      typeof (value as L.LatLngLiteral).lng === 'number'
    );
  }

  private getOrderedMarkers(featureGroup: L.FeatureGroup): L.Marker[] {
    const markers: L.Marker[] = [];
    featureGroup.eachLayer((layer) => {
      if (layer instanceof L.Marker) {
        markers.push(layer);
      }
    });
    return markers.sort(
      (a, b) =>
        leafletAdapter.util.stamp(a as unknown as L.Layer) -
        leafletAdapter.util.stamp(b as unknown as L.Layer),
    );
  }

  private countLatLngNodes(latlngs: unknown): number {
    if (!Array.isArray(latlngs)) return 0;
    if (latlngs.length === 0) return 0;

    if (this.isLatLngLiteral(latlngs[0])) {
      return latlngs.length;
    }

    return latlngs.reduce((sum, item) => sum + this.countLatLngNodes(item), 0);
  }

  private rebuildLatLngStructure<T>(
    original: T,
    markerPositions: L.LatLng[],
    markerIndex: { value: number },
  ): T {
    if (!Array.isArray(original)) {
      return original;
    }

    if (original.length > 0 && this.isLatLngLiteral(original[0])) {
      const ring = original as unknown as L.LatLngLiteral[];
      const hasClosingPoint = ring.length > 1 && this.latLngEquals(ring[0], ring[ring.length - 1]);
      const updated = ring.map((_pt, idx) => {
        const pos = markerPositions[markerIndex.value] ?? ring[idx];
        markerIndex.value += 1;
        return leafletAdapter.createLatLng(pos.lat, pos.lng);
      });

      if (hasClosingPoint && updated.length > 1) {
        updated[updated.length - 1] = updated[0];
      }

      return updated as unknown as T;
    }

    return (original as unknown as unknown[]).map((child) =>
      this.rebuildLatLngStructure(child, markerPositions, markerIndex),
    ) as unknown as T;
  }

  private getOptimizationMetadataFromFeatureGroup(featureGroup: L.FeatureGroup): {
    level: number;
    original: number;
  } {
    let level = 0;
    let original = 0;
    featureGroup.eachLayer((layer) => {
      if (layer instanceof L.Polygon) {
        const poly = layer as PolydrawPolygon;
        if (typeof poly._polydrawOptimizationLevel === 'number') {
          level = poly._polydrawOptimizationLevel || 0;
        }
        if (typeof poly._polydrawOptimizationOriginalLevel === 'number') {
          original = poly._polydrawOptimizationOriginalLevel || 0;
        }
      }
    });
    if (!original && level > 0) {
      original = level;
    }
    return { level, original };
  }

  private getFeatureMetadataState(featureGroup?: L.FeatureGroup): {
    featureId?: string;
    metadata?: Record<string, unknown>;
    sourceFeatureIds?: string[];
    interactionOverride?: LayerInteraction;
    styleOverrides?: PolygonStyleOverrides;
    createdAt?: string;
  } {
    if (!featureGroup) {
      return {};
    }

    const featureMetadata = (featureGroup as PolydrawFeatureGroup)._polydrawMetadata;
    const createdAt =
      featureMetadata?.createdAt instanceof Date &&
      !Number.isNaN(featureMetadata.createdAt.getTime())
        ? featureMetadata.createdAt.toISOString()
        : undefined;

    return {
      featureId: featureMetadata?.id,
      metadata: featureMetadata?.metadata ? { ...featureMetadata.metadata } : undefined,
      sourceFeatureIds: Array.isArray(featureMetadata?.sourceFeatureIds)
        ? [...featureMetadata.sourceFeatureIds]
        : featureMetadata?.id
          ? [featureMetadata.id]
          : undefined,
      interactionOverride: featureMetadata?.interactionOverride,
      styleOverrides: featureMetadata?.styleOverrides
        ? { ...featureMetadata.styleOverrides }
        : undefined,
      createdAt,
    };
  }

  private getOptimizationMetadataFromPolygonLayer(polygon?: L.Polygon): {
    level: number;
    original: number;
  } {
    if (!polygon) {
      return { level: 0, original: 0 };
    }
    const polydrawPolygon = polygon as PolydrawPolygon;
    const level = polydrawPolygon._polydrawOptimizationLevel || 0;
    const original = polydrawPolygon._polydrawOptimizationOriginalLevel || (level > 0 ? level : 0);
    return { level, original };
  }

  private getDistanceMeters(a: L.LatLngLiteral, b: L.LatLngLiteral): number {
    try {
      const pointA = leafletAdapter.createLatLng(a.lat, a.lng);
      const pointB = leafletAdapter.createLatLng(b.lat, b.lng);
      if (typeof pointA.distanceTo === 'function') {
        return pointA.distanceTo(pointB);
      }
    } catch {
      // Fallback to haversine below
    }

    const toRad = (value: number) => (value * Math.PI) / 180;
    const R = 6371000;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);
    const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
    return R * c;
  }

  private findClosestVertexIndex(
    target: L.LatLngLiteral,
    vertices: L.LatLngLiteral[],
  ): number | null {
    if (!vertices || vertices.length === 0) {
      return null;
    }
    let closestIndex = 0;
    let minDistance = Number.POSITIVE_INFINITY;
    for (let i = 0; i < vertices.length; i++) {
      const dist = this.getDistanceMeters(target, vertices[i]);
      if (dist < minDistance) {
        minDistance = dist;
        closestIndex = i;
        if (dist === 0) {
          break;
        }
      }
    }
    return closestIndex;
  }

  private getToleranceForOptimizationLevel(level: number): number {
    const normalized = Math.min(Math.max(level, 0), 10) / 10;
    const visConfig = this.config.markers.visualOptimization ?? {};
    const minTolerance = Math.max(visConfig.toleranceMin ?? 0.000005, 0);
    const maxTolerance = Math.max(visConfig.toleranceMax ?? 0.005, minTolerance);
    const curvePower = visConfig.curve ?? 1.35;
    const curved = Math.pow(normalized, curvePower);
    return minTolerance + (maxTolerance - minTolerance) * curved;
  }

  private elbowClicked(
    e: L.LeafletMouseEvent,
    polygonLayer: L.Polygon,
    forcedLatLng: L.LatLng,
  ): void {
    // Enforce settings
    if (!this.config.modes.edgeDeletion) return;
    if (!this.isEdgeDeletionModifierKeyPressed(e.originalEvent)) return;

    const clickedLatLng = forcedLatLng ?? e.latlng;

    // Normalize Leaflet polygon latlngs to an array of rings: L.LatLng[][]
    const rawLatLngs = polygonLayer.getLatLngs();
    let rings: L.LatLng[][] = [];
    if (Array.isArray(rawLatLngs) && rawLatLngs.length > 0) {
      if (Array.isArray(rawLatLngs[0])) {
        const first = (rawLatLngs as unknown[])[0];
        if (Array.isArray(first) && Array.isArray((first as unknown[])[0])) {
          // MultiPolygon-like structure: [ [ [LatLng] ] ] – flatten first level
          rings = (rawLatLngs as unknown as L.LatLng[][][])[0] as unknown as L.LatLng[][];
        } else {
          // Polygon with holes: [ [LatLng] , [LatLng] ... ]
          rings = rawLatLngs as L.LatLng[][];
        }
      } else {
        // Single ring provided as LatLng[]
        rings = [rawLatLngs as unknown as L.LatLng[]];
      }
    } else {
      return;
    }

    // Find exact matching vertex by equality on lat/lng
    let targetRingIndex = -1;
    let targetVertexIndex = -1;
    for (let r = 0; r < rings.length; r++) {
      const ring = rings[r];
      for (let v = 0; v < ring.length; v++) {
        const p = ring[v];
        if (p.lat === clickedLatLng.lat && p.lng === clickedLatLng.lng) {
          targetRingIndex = r;
          targetVertexIndex = v;
          break;
        }
      }
      if (targetRingIndex !== -1) break;
    }

    if (targetRingIndex === -1 || targetVertexIndex === -1) {
      return;
    }

    const targetRing = rings[targetRingIndex];
    // Require at least 4 points (3 corners + close) – in Leaflet rings are usually open, so use 4 as minimum vertices
    if (targetRing.length <= this.config.interaction.edgeDeletion.minVertices) {
      return;
    }

    // Build new rings with the vertex removed
    const newRings: L.LatLng[][] = rings.map((ring, idx) => {
      if (idx !== targetRingIndex) return ring.slice();
      const nr = ring.slice();
      nr.splice(targetVertexIndex, 1);
      return nr;
    });

    // Convert to GeoJSON coordinates and ensure closure of each ring
    const coords = newRings.map((ring) => {
      const arr = ring.map((ll) => [ll.lng, ll.lat]);
      if (arr.length > 0) {
        const first = arr[0];
        const last = arr[arr.length - 1];
        if (first[0] !== last[0] || first[1] !== last[1]) {
          arr.push([first[0], first[1]]);
        }
      }
      return arr;
    });

    // Remove the current feature group for this polygon
    let currentFeatureGroup: L.FeatureGroup | null = null;
    for (const fg of this.getFeatureGroups()) {
      let found = false;
      fg.eachLayer((layer) => {
        if (layer === polygonLayer) found = true;
      });
      if (found) {
        currentFeatureGroup = fg;
        break;
      }
    }
    const metadata = currentFeatureGroup
      ? this.getOptimizationMetadataFromFeatureGroup(currentFeatureGroup)
      : this.getOptimizationMetadataFromPolygonLayer(polygonLayer);
    const featureMetadataState = this.getFeatureMetadataState(currentFeatureGroup ?? undefined);
    const { level: optimizationLevel, original: originalOptimizationLevel } = metadata;

    if (currentFeatureGroup) {
      this.removeFeatureGroup(currentFeatureGroup);
    }

    const newPolygon = this.turfHelper.getMultiPolygon([coords]);
    this.emitPolygonUpdated({
      operation: 'removeVertex',
      polygon: newPolygon,
      optimizationLevel,
      originalOptimizationLevel,
      featureId: featureMetadataState.featureId,
      featureMetadata: featureMetadataState.metadata,
      sourceFeatureIds: featureMetadataState.sourceFeatureIds,
      featureInteractionOverride: featureMetadataState.interactionOverride,
      featureStyleOverrides: featureMetadataState.styleOverrides,
      featureCreatedAt: featureMetadataState.createdAt,
    });
  }

  private markerDrag(featureGroup: L.FeatureGroup): void {
    if (!this._activeMarker) {
      if (!isTestEnvironment()) {
        console.warn('No active marker set for dragging.');
      }
      return;
    }
    const polygonLayer = featureGroup.getLayers().find((l) => l instanceof L.Polygon) as
      | L.Polygon
      | undefined;

    if (!polygonLayer) {
      if (!isTestEnvironment()) {
        console.warn('No polygon found in feature group for marker drag.');
      }
      return;
    }

    const markers = this.getOrderedMarkers(featureGroup);
    if (markers.length === 0) {
      return;
    }

    const markerPositions = markers.map((m) => m.getLatLng());
    const expectedVertices = this.countLatLngNodes(polygonLayer.getLatLngs());
    if (markerPositions.length < expectedVertices) {
      if (!isTestEnvironment()) {
        console.warn(
          'Not enough markers to rebuild polygon during drag. Expected:',
          expectedVertices,
          'got:',
          markerPositions.length,
        );
      }
      return;
    }

    const markerIndex = { value: 0 };
    const updatedLatLngs = this.rebuildLatLngStructure(
      polygonLayer.getLatLngs(),
      markerPositions,
      markerIndex,
    );

    polygonLayer.setLatLngs(updatedLatLngs as unknown as L.LatLng[][] | L.LatLng[][][]);
  }

  private async markerDragEnd(featureGroup: L.FeatureGroup): Promise<void> {
    this.polygonInformation.deletePolygonInformationStorage();
    const featureCollection = featureGroup.toGeoJSON() as FeatureCollection<Polygon | MultiPolygon>;

    if (!featureCollection.features || featureCollection.features.length === 0) {
      return;
    }

    // const optimizationLevel = this.getOptimizationLevelFromFeatureGroup(featureGroup);

    const { level: optimizationLevel, original: originalOptimizationLevel } =
      this.getOptimizationMetadataFromFeatureGroup(featureGroup);
    const featureMetadataState = this.getFeatureMetadataState(featureGroup);
    const targetLayerId = this.layerManager?.getLayerForFeatureGroup(featureGroup);

    // Remove the current feature group first to avoid duplication
    this.cleanupFeatureGroup(featureGroup);
    this.removeFeatureGroup(featureGroup);

    const emitCleanedPolygon = (
      polygon: Feature<Polygon | MultiPolygon>,
      allowMerge: boolean,
      opts: { optimizationLevel: number; originalOptimizationLevel: number },
    ) => {
      const cleaned = this.turfHelper.removeDuplicateVertices(polygon);
      const parts: Feature<Polygon | MultiPolygon>[] = [];

      if (!this.config.kinks) {
        try {
          const split = this.turfHelper.getKinks(cleaned);
          if (split && split.length > 0) {
            parts.push(...split);
          }
        } catch {
          // ignore and fall back
        }
      }

      if (parts.length === 0) {
        if (cleaned.geometry.type === 'MultiPolygon') {
          cleaned.geometry.coordinates.forEach((coords) =>
            parts.push(this.turfHelper.getMultiPolygon([coords])),
          );
        } else {
          parts.push(cleaned);
        }
      }

      parts.forEach((part) => {
        this.emitPolygonUpdated({
          operation: 'markerDrag',
          polygon: part,
          allowMerge,
          optimizationLevel: opts.optimizationLevel,
          originalOptimizationLevel: opts.originalOptimizationLevel,
          featureId: featureMetadataState.featureId,
          featureMetadata: featureMetadataState.metadata,
          sourceFeatureIds: featureMetadataState.sourceFeatureIds,
          featureInteractionOverride: featureMetadataState.interactionOverride,
          featureStyleOverrides: featureMetadataState.styleOverrides,
          targetLayerId,
          featureCreatedAt: featureMetadataState.createdAt,
        });
      });
    };

    if (featureCollection.features[0].geometry.type === 'MultiPolygon') {
      for (const element of featureCollection.features[0].geometry.coordinates) {
        const feature = this.turfHelper.getMultiPolygon([element]);
        // INTELLIGENT MERGING: Allow merging when polygon intersects with existing structures
        emitCleanedPolygon(feature, true, {
          optimizationLevel,
          originalOptimizationLevel,
        });
      }
    } else {
      const feature = this.turfHelper.getMultiPolygon([
        featureCollection.features[0].geometry.coordinates,
      ]);

      emitCleanedPolygon(feature, true, {
        optimizationLevel,
        originalOptimizationLevel,
      });
    }
    this.polygonInformation.createPolygonInformationStorage(this.getFeatureGroups());
  }

  // Polygon dragging methods
  private onPolygonMouseMove = (e: L.LeafletMouseEvent) => {
    if (
      !this.currentDragPolygon ||
      !this.currentDragPolygon._polydrawDragData ||
      !this.currentDragPolygon._polydrawDragData.isDragging
    )
      return;

    const polygon = this.currentDragPolygon;
    const dragData = polygon._polydrawDragData;

    if (!this.currentDragIsClone) {
      const eventToCheck = e.originalEvent && 'metaKey' in e.originalEvent ? e.originalEvent : e;
      const currentModifierState = this.detectDragSubtractModifierKey(eventToCheck as MouseEvent);
      if (currentModifierState !== this.currentModifierDragMode) {
        this.handleModifierToggleDuringDrag(eventToCheck as MouseEvent);
      }
    }

    const startPos = dragData!.startPosition;
    const currentPos = e.latlng;
    const offsetLat = currentPos.lat - startPos!.lat;
    const offsetLng = currentPos.lng - startPos!.lng;

    const newLatLngs = this.offsetPolygonCoordinates(dragData!.startLatLngs, offsetLat, offsetLng);
    polygon.setLatLngs(newLatLngs);

    this.updateMarkersAndHoleLinesDuringDrag(polygon, offsetLat, offsetLng);
  };

  private onPolygonMouseUp = (e: L.LeafletMouseEvent) => {
    void e; // mark parameter as used to satisfy ESLint while keeping the name
    if (
      !this.currentDragPolygon ||
      !this.currentDragPolygon._polydrawDragData ||
      !this.currentDragPolygon._polydrawDragData.isDragging
    )
      return;

    const polygon = this.currentDragPolygon;
    const dragData = polygon._polydrawDragData;

    dragData!.isDragging = false;

    if (this.map.dragging) {
      this.map.dragging.enable();
    }

    // Restore original opacity
    if (polygon._polydrawDragData && polygon._polydrawDragData.originalOpacity != null) {
      polygon.setStyle({ fillOpacity: polygon._polydrawDragData.originalOpacity });
    }

    this.setMarkerVisibility(polygon, true);

    try {
      const container = this.map.getContainer();
      container.style.cursor = '';
    } catch {
      // Handle DOM errors
    }

    this.clearCloneGhost();
    this.detachDragTouchListeners();
    this.detachDragDocumentListeners();
    this.detachDragCancelHandlers();

    const wasCloneDrag = this.currentDragIsClone;
    this.currentDragIsClone = false;
    if (wasCloneDrag) {
      this.updatePolygonAfterCloneDrag(polygon, dragData);
    } else {
      this.updatePolygonAfterDrag(polygon);
    }

    if (polygon._polydrawOriginalMarkerPositions) {
      polygon._polydrawOriginalMarkerPositions.clear();
      delete polygon._polydrawOriginalMarkerPositions;
    }
    if (polygon._polydrawOriginalHoleLinePositions) {
      polygon._polydrawOriginalHoleLinePositions.clear();
      delete polygon._polydrawOriginalHoleLinePositions;
    }
    if (polygon._polydrawCurrentDragSession) {
      delete polygon._polydrawCurrentDragSession;
    }

    this.currentDragPolygon = null;
  };

  private onPolygonDragCancel = () => {
    this.cancelActivePolygonDrag();
  };

  private onPolygonDragKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    this.cancelActivePolygonDrag();
  };

  private onDragTouchMove = (e: TouchEvent) => {
    e.preventDefault();
    const latlng = EventAdapter.extractCoordinates(
      e as unknown as Record<string, unknown>,
      this.map,
    );
    if (!latlng) return;
    this.onPolygonMouseMove({ latlng } as L.LeafletMouseEvent);
  };

  private onDragTouchEnd = (e: TouchEvent) => {
    e.preventDefault();
    this.onPolygonMouseUp({ latlng: null } as unknown as L.LeafletMouseEvent);
  };

  private readonly onDragDocumentMove = (event: MouseEvent | PointerEvent) => {
    const latlng = EventAdapter.extractCoordinates(
      event as unknown as Record<string, unknown>,
      this.map,
    );
    if (!latlng) {
      return;
    }

    this.onPolygonMouseMove({ latlng, originalEvent: event } as unknown as L.LeafletMouseEvent);
  };

  private readonly onDragDocumentEnd = (event: MouseEvent | PointerEvent) => {
    const latlng =
      EventAdapter.extractCoordinates(event as unknown as Record<string, unknown>, this.map) ??
      null;

    this.onPolygonMouseUp({ latlng, originalEvent: event } as unknown as L.LeafletMouseEvent);
  };

  private attachDragDocumentListeners(): void {
    this.detachDragDocumentListeners();

    this.dragUsesPointerEvents = EventAdapter.shouldUsePointerEvents();
    this._boundDragMoveListener = this.onDragDocumentMove;
    this._boundDragEndListener = this.onDragDocumentEnd;

    if (this.dragUsesPointerEvents) {
      document.addEventListener('pointermove', this._boundDragMoveListener);
      document.addEventListener('pointerup', this._boundDragEndListener);
    }

    document.addEventListener('mousemove', this._boundDragMoveListener);
    document.addEventListener('mouseup', this._boundDragEndListener);
  }

  private detachDragDocumentListeners(): void {
    if (this._boundDragMoveListener) {
      if (this.dragUsesPointerEvents) {
        document.removeEventListener('pointermove', this._boundDragMoveListener);
      }
      document.removeEventListener('mousemove', this._boundDragMoveListener);
      this._boundDragMoveListener = null;
    }

    if (this._boundDragEndListener) {
      if (this.dragUsesPointerEvents) {
        document.removeEventListener('pointerup', this._boundDragEndListener);
      }
      document.removeEventListener('mouseup', this._boundDragEndListener);
      this._boundDragEndListener = null;
    }

    this.dragUsesPointerEvents = false;
  }

  private attachDragTouchListeners(): void {
    this.detachDragTouchListeners();
    this._boundDragTouchMove = this.onDragTouchMove;
    this._boundDragTouchEnd = this.onDragTouchEnd;
    const container = this.map.getContainer();
    container.addEventListener('touchmove', this._boundDragTouchMove, { passive: false });
    container.addEventListener('touchend', this._boundDragTouchEnd, { passive: false });
  }

  private detachDragTouchListeners(): void {
    const container = this.map.getContainer();
    if (this._boundDragTouchMove) {
      container.removeEventListener('touchmove', this._boundDragTouchMove);
      this._boundDragTouchMove = null;
    }
    if (this._boundDragTouchEnd) {
      container.removeEventListener('touchend', this._boundDragTouchEnd);
      this._boundDragTouchEnd = null;
    }
  }

  private detachPolygonTouchStart(polygon: L.Polygon): void {
    const listener = this.polygonTouchStartListeners.get(polygon);
    if (!listener) return;
    const el = (polygon as unknown as { _path?: SVGElement })._path;
    if (el) {
      el.removeEventListener('touchstart', listener as unknown as (e: Event) => void);
    }
    this.polygonTouchStartListeners.delete(polygon);
  }

  private detachPolygonNativeStartListeners(polygon: L.Polygon): void {
    const listeners = this.polygonNativeStartListeners.get(polygon);
    if (!listeners) return;
    const el = (polygon as unknown as { _path?: SVGElement })._path;
    if (el) {
      el.removeEventListener('mousedown', listeners.mousedown, { capture: true });
      el.removeEventListener('pointerdown', listeners.pointerdown, { capture: true });
      el.removeEventListener('touchstart', listeners.touchstart, { capture: true });
    }
    this.polygonNativeStartListeners.delete(polygon);
  }

  private attachDragCancelHandlers(): void {
    if (this.dragCancelHandlersAttached) return;
    this.dragCancelHandlersAttached = true;
    document.addEventListener('keydown', this.onPolygonDragKeyDown);
    (this.map as L.Evented).on(
      'pointercancel',
      this.onPolygonDragCancel as L.LeafletEventHandlerFn,
      this,
    );
    (this.map as L.Evented).on(
      'touchcancel',
      this.onPolygonDragCancel as L.LeafletEventHandlerFn,
      this,
    );
  }

  private detachDragCancelHandlers(): void {
    if (!this.dragCancelHandlersAttached) return;
    this.dragCancelHandlersAttached = false;
    document.removeEventListener('keydown', this.onPolygonDragKeyDown);
    (this.map as L.Evented).off(
      'pointercancel',
      this.onPolygonDragCancel as L.LeafletEventHandlerFn,
      this,
    );
    (this.map as L.Evented).off(
      'touchcancel',
      this.onPolygonDragCancel as L.LeafletEventHandlerFn,
      this,
    );
  }

  private cancelActivePolygonDrag(): void {
    if (!this.currentDragPolygon || !this.currentDragPolygon._polydrawDragData) {
      this.clearCloneGhost();
      this.detachDragDocumentListeners();
      return;
    }

    const polygon = this.currentDragPolygon;
    const dragData = polygon._polydrawDragData;
    if (!dragData) {
      this.clearCloneGhost();
      this.detachDragDocumentListeners();
      return;
    }
    if (!dragData.isDragging) {
      this.clearCloneGhost();
      this.detachDragDocumentListeners();
      return;
    }

    dragData.isDragging = false;

    if (this.map.dragging) {
      this.map.dragging.enable();
    }

    if (dragData.startLatLngs) {
      polygon.setLatLngs(dragData.startLatLngs);
    }

    if (polygon._polydrawOriginalMarkerPositions) {
      polygon._polydrawOriginalMarkerPositions.forEach((pos, marker) => {
        marker.setLatLng(pos);
      });
      polygon._polydrawOriginalMarkerPositions.clear();
      delete polygon._polydrawOriginalMarkerPositions;
    }

    if (polygon._polydrawOriginalHoleLinePositions) {
      polygon._polydrawOriginalHoleLinePositions.forEach((positions, line) => {
        line.setLatLngs(positions);
      });
      polygon._polydrawOriginalHoleLinePositions.clear();
      delete polygon._polydrawOriginalHoleLinePositions;
    }

    if (polygon._polydrawCurrentDragSession) {
      delete polygon._polydrawCurrentDragSession;
    }

    if (dragData.originalOpacity != null) {
      polygon.setStyle({ fillOpacity: dragData.originalOpacity });
    }

    this.setSubtractVisualMode(polygon, false);
    this.setMarkerVisibility(polygon, true);

    try {
      const container = this.map.getContainer();
      container.style.cursor = '';
    } catch {
      // Handle DOM errors
    }

    this.clearCloneGhost();
    this.detachDragTouchListeners();
    this.detachDragDocumentListeners();
    this.detachDragCancelHandlers();
    this.currentDragIsClone = false;
    this.currentModifierDragMode = false;
    this.isModifierKeyHeld = false;
    this.currentDragPolygon = null;
  }

  private offsetPolygonCoordinates(
    latLngs: LeafletPolygonLatLngs,
    offsetLat: number,
    offsetLng: number,
  ): LeafletPolygonLatLngs {
    if (!latLngs) return latLngs as unknown as LeafletPolygonLatLngs;

    // If nested arrays, recurse until we reach arrays of LatLng
    if (Array.isArray((latLngs as unknown as unknown[])[0])) {
      return (latLngs as unknown as (L.LatLng[] | L.LatLng[][])[]).map((ring) =>
        this.offsetPolygonCoordinates(ring as L.LatLng[] | L.LatLng[][], offsetLat, offsetLng),
      ) as L.LatLng[][] | L.LatLng[][][];
    }

    // Base case: array of LatLng -> return array of shifted LatLng
    return (latLngs as L.LatLng[]).map((p) =>
      leafletAdapter.createLatLng(p.lat + offsetLat, p.lng + offsetLng),
    );
  }

  private updateMarkersAndHoleLinesDuringDrag(
    polygon: PolydrawPolygon,
    offsetLat: number,
    offsetLng: number,
  ): void {
    try {
      let featureGroup: L.FeatureGroup | null = null;

      // Find the specific feature group that contains this exact polygon
      for (const fg of this.getFeatureGroups()) {
        let foundPolygon = false;
        fg.eachLayer((layer) => {
          if (layer === polygon) {
            foundPolygon = true;
          }
        });
        if (foundPolygon) {
          featureGroup = fg;
          break;
        }
      }

      if (!featureGroup) {
        return;
      }

      // Ensure this drag session is unique to this specific polygon
      const dragSessionKey =
        '_polydrawDragSession_' + Date.now() + '_' + leafletAdapter.util.stamp(polygon);
      if (!polygon._polydrawCurrentDragSession) {
        polygon._polydrawCurrentDragSession = dragSessionKey;
        polygon._polydrawOriginalMarkerPositions = new Map();
        polygon._polydrawOriginalHoleLinePositions = new Map();

        // Only store markers and lines that belong to THIS specific feature group
        featureGroup.eachLayer((layer) => {
          if (layer instanceof L.Marker) {
            polygon._polydrawOriginalMarkerPositions!.set(layer, layer.getLatLng());
          } else if (layer instanceof L.Polyline && !(layer instanceof L.Polygon)) {
            const latLngs = layer.getLatLngs() as unknown as L.LatLng[];
            polygon._polydrawOriginalHoleLinePositions!.set(layer, latLngs);
          }
        });
      }

      // Only update markers and lines that belong to THIS specific feature group
      // and that were stored in the original positions map
      featureGroup.eachLayer((layer) => {
        if (layer instanceof L.Marker) {
          const originalPos = polygon._polydrawOriginalMarkerPositions!.get(layer);
          if (originalPos) {
            const newLatLng = {
              lat: originalPos.lat + offsetLat,
              lng: originalPos.lng + offsetLng,
            };
            layer.setLatLng(newLatLng);
          }
        } else if (layer instanceof L.Polyline && !(layer instanceof L.Polygon)) {
          const originalPositions = polygon._polydrawOriginalHoleLinePositions!.get(layer) as
            | L.LatLng[]
            | L.LatLng[][]
            | undefined;
          if (originalPositions) {
            let newLatLngs: L.LatLng[] | L.LatLng[][];

            if (Array.isArray((originalPositions as unknown as unknown[])[0])) {
              // MultiPolyline: LatLng[][]
              newLatLngs = (originalPositions as L.LatLng[][]).map((ring) =>
                ring.map((latlng) =>
                  leafletAdapter.createLatLng(latlng.lat + offsetLat, latlng.lng + offsetLng),
                ),
              );
            } else {
              // Simple Polyline: LatLng[]
              newLatLngs = (originalPositions as L.LatLng[]).map((latlng) =>
                leafletAdapter.createLatLng(latlng.lat + offsetLat, latlng.lng + offsetLng),
              );
            }

            layer.setLatLngs(newLatLngs);
          }
        }
      });
    } catch (error) {
      // Silently handle errors
      console.warn('Error updating markers during drag:', error);
    }
  }

  private async updatePolygonAfterDrag(polygon: PolydrawPolygon): Promise<void> {
    try {
      let featureGroup: L.FeatureGroup | null = null;

      for (const fg of this.getFeatureGroups()) {
        fg.eachLayer((layer) => {
          if (layer === polygon) {
            featureGroup = fg;
          }
        });
        if (featureGroup) break;
      }

      if (!featureGroup) {
        return;
      }

      const newGeoJSON = polygon.toGeoJSON();

      if (this.isModifierDragActive()) {
        this.performModifierSubtract(newGeoJSON, featureGroup);
        this.currentModifierDragMode = false;
        this.isModifierKeyHeld = false;
        return;
      }

      const { level: optimizationLevel, original: originalOptimizationLevel } =
        this.getOptimizationMetadataFromFeatureGroup(featureGroup);
      const featureMetadataState = this.getFeatureMetadataState(featureGroup);
      this.cleanupFeatureGroup(featureGroup);
      this.removeFeatureGroup(featureGroup);

      const feature = this.turfHelper.getTurfPolygon(newGeoJSON);
      this.emitPolygonUpdated({
        operation: 'polygonDrag',
        polygon: feature,
        allowMerge: true,
        optimizationLevel,
        originalOptimizationLevel,
        featureId: featureMetadataState.featureId,
        featureMetadata: featureMetadataState.metadata,
        sourceFeatureIds: featureMetadataState.sourceFeatureIds,
        featureInteractionOverride: featureMetadataState.interactionOverride,
        featureStyleOverrides: featureMetadataState.styleOverrides,
        featureCreatedAt: featureMetadataState.createdAt,
      });

      this.polygonInformation.createPolygonInformationStorage(this.getFeatureGroups());
    } catch {
      // Handle errors
    }
  }

  private updatePolygonAfterCloneDrag(
    polygon: PolydrawPolygon,
    dragData: PolydrawPolygon['_polydrawDragData'],
  ): void {
    try {
      let featureGroup: L.FeatureGroup | null = null;

      for (const fg of this.getFeatureGroups()) {
        fg.eachLayer((layer) => {
          if (layer === polygon) {
            featureGroup = fg;
          }
        });
        if (featureGroup) break;
      }

      if (!featureGroup) {
        return;
      }

      const newGeoJSON = polygon.toGeoJSON() as Feature<Polygon | MultiPolygon>;
      const originalLatLngs = (dragData?.startLatLngs ?? null) as LeafletPolygonLatLngs | null;
      const originalGeoJSON = originalLatLngs
        ? this.createPolygonFeatureFromLatLngs(originalLatLngs)
        : null;

      const { level: optimizationLevel, original: originalOptimizationLevel } =
        this.getOptimizationMetadataFromFeatureGroup(featureGroup);
      const featureMetadataState = this.getFeatureMetadataState(featureGroup);

      this.cleanupFeatureGroup(featureGroup);
      this.removeFeatureGroup(featureGroup);

      const movedFeature = this.turfHelper.getTurfPolygon(newGeoJSON);
      this.emitPolygonUpdated({
        operation: 'polygonDrag',
        polygon: movedFeature,
        allowMerge: true,
        optimizationLevel,
        originalOptimizationLevel,
        featureId: featureMetadataState.featureId,
        featureMetadata: featureMetadataState.metadata,
        sourceFeatureIds: featureMetadataState.sourceFeatureIds,
        featureInteractionOverride: featureMetadataState.interactionOverride,
        featureStyleOverrides: featureMetadataState.styleOverrides,
        featureCreatedAt: featureMetadataState.createdAt,
      });

      if (originalGeoJSON) {
        const originalFeature = this.turfHelper.getTurfPolygon(originalGeoJSON);
        this.emitPolygonUpdated({
          operation: 'polygonClone',
          polygon: originalFeature,
          allowMerge: true,
          optimizationLevel,
          originalOptimizationLevel,
          featureId: featureMetadataState.featureId,
          featureMetadata: featureMetadataState.metadata,
          sourceFeatureIds: featureMetadataState.sourceFeatureIds,
          featureInteractionOverride: featureMetadataState.interactionOverride,
          featureStyleOverrides: featureMetadataState.styleOverrides,
          featureCreatedAt: featureMetadataState.createdAt,
        });
      }

      this.polygonInformation.createPolygonInformationStorage(this.getFeatureGroups());
    } catch {
      // Handle errors
    } finally {
      this.currentModifierDragMode = false;
      this.isModifierKeyHeld = false;
    }
  }

  private showCloneGhost(latLngs: LeafletPolygonLatLngs | null): void {
    this.clearCloneGhost();
    if (!latLngs) return;

    try {
      const ghost = leafletAdapter.createPolygon(latLngs, {
        color: this.config.styles.polygon.color,
        weight: this.config.styles.polygon.weight,
        opacity: 0.9,
        fill: false,
        fillOpacity: 0,
        dashArray: '4,6',
        interactive: false,
      });
      ghost.addTo(this.map);
      this.currentCloneGhost = ghost;
    } catch {
      // Handle ghost creation errors
    }
  }

  private clearCloneGhost(): void {
    if (!this.currentCloneGhost) return;
    try {
      this.map.removeLayer(this.currentCloneGhost);
    } catch {
      // Ignore removal errors
    }
    this.currentCloneGhost = null;
  }

  private createPolygonFeatureFromLatLngs(
    latLngs: LeafletPolygonLatLngs,
  ): Feature<Polygon | MultiPolygon> | null {
    try {
      const polygon = leafletAdapter.createPolygon(latLngs);
      return polygon.toGeoJSON() as Feature<Polygon | MultiPolygon>;
    } catch {
      return null;
    }
  }

  private getDragSubtractModifierKey(): ModifierKey {
    const { keys } = this.config.interaction.drag.modifierSubtract;
    const userAgent = navigator.userAgent.toLowerCase();
    const isMac = userAgent.includes('mac');
    const isWindows = userAgent.includes('windows');

    if (isMac && keys.mac) return keys.mac;
    if (isWindows && keys.windows) return keys.windows;
    if (keys.linux) return keys.linux;

    return isMac ? 'metaKey' : 'ctrlKey';
  }

  private detectDragSubtractModifierKey(event: MouseEvent | KeyboardEvent | PointerEvent): boolean {
    if (isTouchDevice()) {
      return false;
    }

    const modifierKey = this.getDragSubtractModifierKey();
    // Type-guarded check for both MouseEvent and KeyboardEvent
    return (
      ((event as MouseEvent | KeyboardEvent | PointerEvent)[
        modifierKey as keyof (MouseEvent | KeyboardEvent | PointerEvent)
      ] as boolean) || false
    );
  }

  private setSubtractVisualMode(polygon: L.Polygon, enabled: boolean): void {
    if (!polygon || !polygon.setStyle) {
      return;
    }

    try {
      if (enabled) {
        polygon.setStyle({
          color: this.config.styles.ui.dragSubtract.color,
        });
      } else {
        polygon.setStyle({
          color: this.config.styles.polygon.color,
        });
      }
      this.updateMarkerColorsForSubtractMode(polygon, enabled);
    } catch {
      // Handle DOM errors
    }
  }

  private updateMarkerColorsForSubtractMode(polygon: L.Polygon, subtractMode: boolean): void {
    try {
      let featureGroup: L.FeatureGroup | null = null;

      for (const fg of this.getFeatureGroups()) {
        fg.eachLayer((layer) => {
          if (layer === polygon) {
            featureGroup = fg;
          }
        });
        if (featureGroup) break;
      }

      if (!featureGroup) return;
      const fg: L.FeatureGroup = featureGroup;

      const hideMarkersOnDrag = this.config.interaction.drag.modifierSubtract.hideMarkersOnDrag;

      fg.eachLayer((layer) => {
        if (layer instanceof L.Marker) {
          const marker = layer as L.Marker;
          const element = marker.getElement();

          if (element) {
            if (subtractMode) {
              if (hideMarkersOnDrag) {
                element.style.display = 'none';
                element.classList.add('subtract-mode-hidden');
              } else {
                element.style.backgroundColor = this.config.styles.ui.dragSubtract.color;
                element.style.borderColor = this.config.styles.ui.dragSubtract.color;
                element.classList.add('subtract-mode');
              }
            } else {
              if (hideMarkersOnDrag) {
                element.style.display = '';
                element.classList.remove('subtract-mode-hidden');
              } else {
                element.style.backgroundColor = '';
                element.style.borderColor = '';
                element.classList.remove('subtract-mode');
              }
            }
          }
        }
      });
    } catch {
      // Handle errors
    }
  }

  private handleModifierToggleDuringDrag(event: MouseEvent): void {
    const isModifierPressed = this.detectDragSubtractModifierKey(event);

    this.currentModifierDragMode = isModifierPressed;
    this.isModifierKeyHeld = isModifierPressed;

    if (this.currentDragPolygon) {
      this.setSubtractVisualMode(this.currentDragPolygon, isModifierPressed);
    }
  }

  private isModifierDragActive(): boolean {
    return this.currentModifierDragMode;
  }

  private performModifierSubtract(
    draggedGeoJSON: Feature<Polygon | MultiPolygon>,
    originalFeatureGroup: L.FeatureGroup,
  ): void {
    try {
      const draggedPolygon = this.turfHelper.getTurfPolygon(draggedGeoJSON);
      const intersectingFeatureGroups: L.FeatureGroup[] = [];
      const sourceLayerId = this.layerManager?.getLayerForFeatureGroup(originalFeatureGroup);

      // Find all feature groups that intersect with the dragged polygon
      this.getFeatureGroups().forEach((featureGroup) => {
        if (featureGroup === originalFeatureGroup) {
          return;
        }
        if (sourceLayerId && this.layerManager) {
          const candidateLayerId = this.layerManager.getLayerForFeatureGroup(featureGroup);
          if (candidateLayerId !== sourceLayerId) {
            return;
          }
        }

        try {
          const featureCollection = featureGroup.toGeoJSON() as FeatureCollection<
            Polygon | MultiPolygon
          >;
          if (!featureCollection || !featureCollection.features || !featureCollection.features[0]) {
            return;
          }

          const firstFeature = featureCollection.features[0] as Feature<Polygon | MultiPolygon>;
          if (!firstFeature.geometry || !firstFeature.geometry.coordinates) {
            return;
          }

          const existingPolygon = this.turfHelper.getTurfPolygon(firstFeature);

          // Check intersection directly using geometry operations
          try {
            const intersection = this.turfHelper.getIntersection(existingPolygon, draggedPolygon);
            if (intersection && intersection.geometry && 'coordinates' in intersection.geometry) {
              const coords = (intersection.geometry as Polygon | MultiPolygon).coordinates;
              if (coords.length > 0) {
                intersectingFeatureGroups.push(featureGroup);
              }
            }
          } catch {
            // If intersection fails, try the polygonIntersect method
            try {
              const hasIntersection = this.turfHelper.polygonIntersect(
                existingPolygon,
                draggedPolygon,
              );
              if (hasIntersection) {
                intersectingFeatureGroups.push(featureGroup);
              }
            } catch {
              // Continue with other polygons
            }
          }
        } catch {
          // Handle errors
        }
      });

      // Remove the original dragged polygon first
      this.removeFeatureGroup(originalFeatureGroup);

      // For each intersecting polygon, subtract the dragged polygon from it
      // This creates the "bite taken" effect
      intersectingFeatureGroups.forEach((featureGroup) => {
        try {
          const featureCollection = featureGroup.toGeoJSON() as FeatureCollection<
            Polygon | MultiPolygon
          >;
          const existingPolygon = this.turfHelper.getTurfPolygon(featureCollection.features[0]);
          const { level: optimizationLevel, original: originalOptimizationLevel } =
            this.getOptimizationMetadataFromFeatureGroup(featureGroup);
          const featureMetadataState = this.getFeatureMetadataState(featureGroup);

          // Remove the existing polygon before creating the result
          this.cleanupFeatureGroup(featureGroup);
          this.removeFeatureGroup(featureGroup);

          // Perform the subtraction: existing polygon - dragged polygon
          try {
            const difference = this.turfHelper.polygonDifference(existingPolygon, draggedPolygon);

            if (difference && difference.geometry) {
              // Use the same approach as regular subtract: create separate polygons for each result
              // Get the coordinates and create individual polygons
              const coords = this.turfHelper.getCoords(difference);

              // Create separate polygons for each coordinate set (like regular subtract does)
              for (const coordSet of coords) {
                const individualPolygon = this.turfHelper.getMultiPolygon([coordSet]);

                // Emit each result polygon separately to ensure they get separate feature groups
                this.emitPolygonUpdated({
                  operation: 'modifierSubtract',
                  polygon: this.turfHelper.getTurfPolygon(individualPolygon),
                  allowMerge: false, // Don't merge the result of subtract operations
                  optimizationLevel,
                  originalOptimizationLevel,
                  featureId: featureMetadataState.featureId,
                  featureMetadata: featureMetadataState.metadata,
                  sourceFeatureIds: featureMetadataState.sourceFeatureIds,
                  featureInteractionOverride: featureMetadataState.interactionOverride,
                  featureStyleOverrides: featureMetadataState.styleOverrides,
                  featureCreatedAt: featureMetadataState.createdAt,
                });
              }
            }
          } catch (differenceError) {
            if (!isTestEnvironment()) {
              console.warn('Failed to perform difference operation:', differenceError);
            }
            // If difference fails, try to add the original polygon back
            this.emitPolygonUpdated({
              operation: 'modifierSubtractFallback',
              polygon: existingPolygon,
              allowMerge: false,
              optimizationLevel,
              originalOptimizationLevel,
              featureId: featureMetadataState.featureId,
              featureMetadata: featureMetadataState.metadata,
              sourceFeatureIds: featureMetadataState.sourceFeatureIds,
              featureInteractionOverride: featureMetadataState.interactionOverride,
              featureStyleOverrides: featureMetadataState.styleOverrides,
              featureCreatedAt: featureMetadataState.createdAt,
            });
          }
        } catch (error) {
          if (!isTestEnvironment()) {
            console.warn('Error in modifier subtract operation:', error);
          }
        }
      });

      // Update polygon information after the operation
      this.polygonInformation.createPolygonInformationStorage(this.getFeatureGroups());
    } catch (error) {
      if (!isTestEnvironment()) {
        console.warn('Error in performModifierSubtract:', error);
      }
    }
  }

  private getEdgeDeletionModifierKey(): ModifierKey {
    const { keys } = this.config.interaction.edgeDeletion;
    const userAgent = navigator.userAgent.toLowerCase();
    const isMac = userAgent.includes('mac');
    const isWindows = userAgent.includes('windows');

    if (isMac && keys.mac) return keys.mac;
    if (isWindows && keys.windows) return keys.windows;
    if (keys.linux) return keys.linux;

    return isMac ? 'metaKey' : 'ctrlKey';
  }

  private isEdgeDeletionModifierKeyPressed(event: MouseEvent): boolean {
    if (isTouchDevice()) {
      return false;
    }
    const modifierKey = this.getEdgeDeletionModifierKey();
    return !!event[modifierKey as keyof MouseEvent];
  }

  private registerPolygonLayerMappings(featureGroup: L.FeatureGroup): void {
    featureGroup.eachLayer((layer) => {
      if (layer instanceof L.Polygon) {
        this.polygonFeatureGroupMap.set(layer, featureGroup);
      }
    });
  }

  private findFeatureGroupForPolygon(polygon: L.Polygon): L.FeatureGroup | undefined {
    for (const featureGroup of this.getFeatureGroups()) {
      let found = false;
      featureGroup.eachLayer((layer) => {
        if (!found && layer === polygon) {
          found = true;
        }
      });
      if (found) {
        return featureGroup;
      }
    }
    return undefined;
  }

  private onMarkerHoverForEdgeDeletion(marker: L.Marker, isHovering: boolean): void {
    const element = marker.getElement();
    if (!element) return;

    if (isHovering) {
      // Add event listeners to detect modifier key state in real-time
      const checkModifierAndUpdate = (e: Event) => {
        const isModifierPressed = this.isEdgeDeletionModifierKeyPressed(e as MouseEvent);
        if (isModifierPressed) {
          element.style.backgroundColor = this.config.styles.ui.edgeDeletion.color;
          element.style.borderColor = this.config.styles.ui.edgeDeletion.color;
          element.classList.add('edge-deletion-hover');
          // Set cursor to pointer when modifier key is held over marker
          try {
            const container = this.map.getContainer();
            container.style.cursor = 'pointer';
          } catch {
            // Handle DOM errors
          }
        } else {
          element.style.backgroundColor = '';
          element.style.borderColor = '';
          element.classList.remove('edge-deletion-hover');
          // Reset cursor when no modifier key
          try {
            const container = this.map.getContainer();
            container.style.cursor = '';
          } catch {
            // Handle DOM errors
          }
        }
      };

      // Check initial state
      const initialEvent = new MouseEvent('mouseover');
      checkModifierAndUpdate(initialEvent);

      // Store the handler for cleanup in WeakMap
      this.markerModifierHandlers.set(marker, checkModifierAndUpdate);

      // Listen for keyboard events to update in real-time
      document.addEventListener('keydown', checkModifierAndUpdate);
      document.addEventListener('keyup', checkModifierAndUpdate);
      element.addEventListener('mousemove', checkModifierAndUpdate);
    } else {
      // Clean up when not hovering
      element.style.backgroundColor = '';
      element.style.borderColor = '';
      element.classList.remove('edge-deletion-hover');

      // Reset cursor
      try {
        const container = this.map.getContainer();
        container.style.cursor = '';
      } catch {
        // Handle DOM errors
      }

      // Remove event listeners
      const handler = this.markerModifierHandlers.get(marker);
      if (handler) {
        document.removeEventListener('keydown', handler);
        document.removeEventListener('keyup', handler);
        element.removeEventListener('mousemove', handler);
        this.markerModifierHandlers.delete(marker);
      }
    }
  }

  private onMarkerHoverForEdgeDeletionEvent = (e: Event) => {
    if (!this.isModifierKeyHeld) return;

    const element = e.target as HTMLElement;
    if (element) {
      element.style.backgroundColor = this.config.styles.ui.edgeDeletion.color;
      element.style.borderColor = this.config.styles.ui.edgeDeletion.color;
      element.classList.add('edge-deletion-hover');
    }
  };

  private onMarkerLeaveForEdgeDeletionEvent = (e: Event) => {
    const element = e.target as HTMLElement;
    if (element) {
      element.style.backgroundColor = '';
      element.style.borderColor = '';
      element.classList.remove('edge-deletion-hover');
    }
  };

  // Helper methods
  private getMarkerIndex(latlngs: L.LatLngLiteral[], position: MarkerPosition): number {
    const bounds: L.LatLngBounds = PolyDrawUtil.getBounds(latlngs, Math.sqrt(2) / 2);
    const compass = new Compass(
      bounds.getSouth(),
      bounds.getWest(),
      bounds.getNorth(),
      bounds.getEast(),
    );
    const compassDirection = compass.getDirection(position);
    const latLngPoint: L.LatLngLiteral = {
      lat: compassDirection.lat,
      lng: compassDirection.lng,
    };
    const targetPoint = this.turfHelper.getCoord(latLngPoint);
    const fc = this.turfHelper.getFeaturePointCollection(latlngs);
    const nearestPointIdx = this.turfHelper.getNearestPointIndex(
      targetPoint,
      fc as FeatureCollection<Point>,
    );
    return nearestPointIdx;
  }

  private ensureMarkerSeparation(
    polygonLength: number,
    markers: {
      menu: { index: number; enabled: boolean };
      delete: { index: number; enabled: boolean };
      info: { index: number; enabled: boolean };
    },
  ): { menu: number; delete: number; info: number } {
    const enabledMarkers: Array<{ type: string; index: number }> = [];

    if (markers.menu.enabled) {
      enabledMarkers.push({ type: 'menu', index: markers.menu.index });
    }
    if (markers.delete.enabled) {
      enabledMarkers.push({ type: 'delete', index: markers.delete.index });
    }
    if (markers.info.enabled) {
      enabledMarkers.push({ type: 'info', index: markers.info.index });
    }

    // If less than 2 markers enabled, no overlap possible
    if (enabledMarkers.length < 2) {
      return {
        menu: markers.menu.index,
        delete: markers.delete.index,
        info: markers.info.index,
      };
    }

    // Check for overlaps and resolve them
    const resolvedIndices = { ...markers };
    const usedIndices = new Set<number>();

    // Process markers in priority order: info, delete, menu
    const processingOrder = ['info', 'delete', 'menu'];

    for (const markerType of processingOrder) {
      const marker = resolvedIndices[markerType as keyof typeof resolvedIndices];

      if (!marker.enabled) continue;

      // If this index is already used, find a new one
      if (usedIndices.has(marker.index)) {
        const newIndex = this.findAlternativeMarkerPosition(
          polygonLength,
          marker.index,
          usedIndices,
        );
        resolvedIndices[markerType as keyof typeof resolvedIndices].index = newIndex;
        usedIndices.add(newIndex);
      } else {
        usedIndices.add(marker.index);
      }
    }

    return {
      menu: resolvedIndices.menu.index,
      delete: resolvedIndices.delete.index,
      info: resolvedIndices.info.index,
    };
  }

  private findAlternativeMarkerPosition(
    polygonLength: number,
    originalIndex: number,
    usedIndices: Set<number>,
  ): number {
    const maxAttempts = polygonLength;
    const step = Math.max(1, Math.floor(polygonLength / 8)); // Try every 1/8th of polygon

    // Try positions moving away from the original index
    for (let attempt = 1; attempt < maxAttempts; attempt++) {
      // Try both directions from original position
      const candidates = [
        (originalIndex + attempt * step) % polygonLength,
        (originalIndex - attempt * step + polygonLength) % polygonLength,
      ];

      for (const candidate of candidates) {
        if (!usedIndices.has(candidate)) {
          return candidate;
        }
      }
    }

    // Fallback: find any unused index
    for (let i = 0; i < polygonLength; i++) {
      if (!usedIndices.has(i)) {
        return i;
      }
    }

    // Ultimate fallback: return original index (shouldn't happen in practice)
    return originalIndex;
  }

  private createDivIcon(processedClasses: string[]): L.DivIcon {
    return IconFactory.createDivIcon(processedClasses);
  }

  private getLatLngInfoString(latlng: L.LatLngLiteral): string {
    return 'Latitude: ' + latlng.lat + ' Longitude: ' + latlng.lng;
  }

  private setMarkerVisibility(polygon: PolydrawPolygon, visible: boolean): void {
    const featureGroup = this.getFeatureGroups().find((fg) => fg.hasLayer(polygon));
    if (!featureGroup) return;

    featureGroup.eachLayer((layer) => {
      if (layer instanceof L.Marker) {
        const marker = layer as L.Marker;
        const element = marker.getElement();
        if (element) {
          const behavior = this.config.interaction.drag.markerBehavior;
          const duration = this.config.interaction.drag.markerAnimationDuration;

          if (behavior === 'hide') {
            element.style.display = visible ? '' : 'none';
          } else if (behavior === 'fade') {
            element.style.transition = `opacity ${duration}ms ease`;
            element.style.opacity = visible ? '1' : '0';
          }
        }
      }
    });
  }

  private setTransformAuxiliaryVisibility(featureGroup: L.FeatureGroup, visible: boolean): void {
    const polygon = this.getPolygonLayer(featureGroup) as PolydrawPolygon | undefined;
    if (polygon) {
      this.setMarkerVisibility(polygon, visible);
    }

    featureGroup.eachLayer((layer) => {
      if (!this.isPolydrawAuxiliaryPolyline(layer)) {
        return;
      }

      const element = layer.getElement?.() as HTMLElement | SVGElement | undefined;
      if (!element) {
        return;
      }

      if (!visible) {
        if (!this.transformAuxiliaryDisplayValues.has(layer)) {
          this.transformAuxiliaryDisplayValues.set(layer, element.style.display);
        }
        element.style.display = 'none';
        return;
      }

      if (this.transformAuxiliaryDisplayValues.has(layer)) {
        element.style.display = this.transformAuxiliaryDisplayValues.get(layer) ?? '';
        this.transformAuxiliaryDisplayValues.delete(layer);
      }
    });
  }

  private isPolydrawAuxiliaryPolyline(layer: L.Layer): layer is PolydrawAuxiliaryPolyline {
    return (
      layer instanceof L.Polyline &&
      !(layer instanceof L.Polygon) &&
      (layer as PolydrawAuxiliaryPolyline)._polydrawAuxiliaryLayer === true
    );
  }

  private getBuiltInMenuButtonConfigs(): BuiltInMenuButtonConfig[] {
    const menuOps = this.config.polygonTools;

    return [
      {
        enabled: menuOps.simplify.enabled,
        id: 'simplify',
        label: 'Simplify',
        classNames: ['simplify'],
      },
      {
        enabled: menuOps.doubleElbows.enabled,
        id: 'doubleElbows',
        label: 'DoubleElbows',
        classNames: ['double-elbows'],
      },
      {
        enabled: menuOps.bbox.enabled,
        id: 'bbox',
        label: 'Bounding box',
        classNames: ['bbox'],
      },
      {
        enabled: menuOps.bezier.enabled,
        id: 'bezier',
        label: 'Curve',
        classNames: ['bezier'],
      },
      {
        enabled: menuOps.scale.enabled,
        id: 'scale',
        label: 'Scale',
        classNames: ['transform-scale'],
      },
      {
        enabled: menuOps.rotate.enabled,
        id: 'rotate',
        label: 'Rotate',
        classNames: ['transform-rotate'],
      },
    ];
  }

  private getBuiltInMenuButtons(featureGroup: L.FeatureGroup): HTMLDivElement[] {
    const buttons = this.getBuiltInMenuButtonConfigs()
      .filter(({ enabled }) => enabled)
      .map(({ id, label, classNames }) => PopupFactory.createMenuButton(id, label, classNames));

    const menuOps = this.config.polygonTools;
    if (menuOps.donut?.enabled && !this.donutTargetHasHoles(featureGroup)) {
      buttons.push(PopupFactory.createMenuButton('donut', 'Donut', ['transform-donut']));
    }

    const optimizationButton = this.getVisualOptimizationToggleButton(featureGroup);
    if (optimizationButton) {
      buttons.push(optimizationButton);
    }

    return buttons;
  }

  private getVisualOptimizationToggleButton(featureGroup: L.FeatureGroup): HTMLDivElement | null {
    if (!this.config.polygonTools.visualOptimizationToggle?.enabled) {
      return null;
    }

    const { level, original } = this.getOptimizationMetadataFromFeatureGroup(featureGroup);
    const hasOptimization = original > 0 || level > 0;
    if (!hasOptimization) {
      return null;
    }

    const isOptimized = typeof level === 'number' && level > 0;
    const optimizationStateClass = isOptimized
      ? 'visual-optimization-state-hidden'
      : 'visual-optimization-state-visible';
    const toggleTitle = isOptimized ? 'Show all markers' : 'Hide extra markers';

    return PopupFactory.createMenuButton('toggleOptimization', toggleTitle, [
      'toggle-visual-optimization',
      optimizationStateClass,
    ]);
  }

  private getPolygonMenuActionButtons(featureGroup: L.FeatureGroup): HTMLDivElement[] {
    const { menuActions } = this.config.polygonTools;
    if (!Array.isArray(menuActions)) {
      return [];
    }

    return menuActions
      .filter((polygonMenuAction) =>
        this.shouldShowPolygonMenuActionButton(polygonMenuAction, featureGroup),
      )
      .map((polygonMenuAction) =>
        PopupFactory.createMenuButton(
          polygonMenuAction.id,
          polygonMenuAction.label,
          this.getPolygonMenuActionClassNames(polygonMenuAction),
        ),
      );
  }

  private shouldShowPolygonMenuActionButton(
    polygonMenuAction: PolygonMenuAction,
    featureGroup: L.FeatureGroup,
  ): boolean {
    if (!polygonMenuAction.id || BUILT_IN_POLYGON_MENU_ACTION_IDS.has(polygonMenuAction.id)) {
      return false;
    }

    if (!polygonMenuAction.visible) {
      return true;
    }

    try {
      return polygonMenuAction.visible(this.getMenuActionContext(featureGroup));
    } catch {
      return false;
    }
  }

  private getPolygonMenuActionClassNames(polygonMenuAction: PolygonMenuAction): string[] {
    if (Array.isArray(polygonMenuAction.className)) {
      return polygonMenuAction.className;
    }

    if (polygonMenuAction.className) {
      return [polygonMenuAction.className];
    }

    return ['menu-action-default'];
  }

  private closeOpenMenuPopup(): void {
    if (this._openMenuPopup) {
      this.map.closePopup(this._openMenuPopup);
      this._openMenuPopup = null;
    }
  }

  private attachMenuPopupCloseHandler(outerWrapper: HTMLElement): void {
    const closeBtn = outerWrapper.querySelector('.marker-menu-close');
    if (!closeBtn) {
      return;
    }

    const handleClose = (event: Event) => {
      event.stopPropagation();
      event.preventDefault();
      if (this._openMenuPopup) {
        this.closeOpenMenuPopup();
      } else {
        this.map.closePopup();
      }
    };

    closeBtn.addEventListener('click', handleClose);
    closeBtn.addEventListener('touchend', handleClose, { passive: false });
  }

  private emitBuiltInPolygonMenuAction(
    action: BuiltInPolygonMenuAction,
    latLngs: L.LatLngLiteral[],
    featureGroup: L.FeatureGroup,
  ): void {
    this.eventManager.emit('polydraw:menu:action', {
      action,
      latLngs,
      featureGroup,
    });
    this.closeOpenMenuPopup();
  }

  private attachBuiltInMenuActionHandler(
    button: HTMLDivElement,
    action: BuiltInPolygonMenuAction,
    latLngs: L.LatLngLiteral[],
    featureGroup: L.FeatureGroup,
  ): void {
    const handler = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      this.emitBuiltInPolygonMenuAction(action, latLngs, featureGroup);
    };

    button.addEventListener('touchend', handler, { passive: false });
    button.onclick = handler;
  }

  private attachTransformHandler(
    button: HTMLDivElement,
    mode: PolygonTransformMode,
    featureGroup: L.FeatureGroup,
  ): void {
    const handler = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      this.startPolygonTransform(featureGroup, mode);
      this.closeOpenMenuPopup();
    };

    button.addEventListener('touchend', handler, { passive: false });
    button.onclick = handler;
  }

  private attachOptimizationToggleHandler(
    button: HTMLDivElement,
    featureGroup: L.FeatureGroup,
  ): void {
    const handler = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      this.toggleOptimizationVisibility(featureGroup);
      this.closeOpenMenuPopup();
    };

    button.addEventListener('touchend', handler, { passive: false });
    button.onclick = handler;
  }

  private attachCustomPolygonMenuActionHandler(
    button: HTMLDivElement,
    actionId: string,
    latLngs: L.LatLngLiteral[],
    featureGroup: L.FeatureGroup,
  ): void {
    const polygonMenuAction = this.config.polygonTools.menuActions?.find(
      ({ id }) => id === actionId,
    );
    if (!polygonMenuAction) {
      return;
    }

    const handler = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      this.eventManager.emit('polydraw:menu:action', {
        action: 'polygonMenuAction',
        menuActionId: actionId,
        latLngs,
        featureGroup,
      });
      this.closeOpenMenuPopup();
    };

    button.addEventListener('touchend', handler, { passive: false });
    button.onclick = handler;
  }

  private attachMenuButtonHandler(
    button: HTMLDivElement,
    latLngs: L.LatLngLiteral[],
    featureGroup: L.FeatureGroup,
  ): void {
    const actionId = button.dataset.actionId;

    switch (actionId) {
      case 'simplify':
      case 'doubleElbows':
      case 'bbox':
      case 'bezier':
        this.attachBuiltInMenuActionHandler(button, actionId, latLngs, featureGroup);
        return;
      case 'scale':
      case 'rotate':
      case 'donut':
        this.attachTransformHandler(button, actionId, featureGroup);
        return;
      case 'toggleOptimization':
        this.attachOptimizationToggleHandler(button, featureGroup);
        return;
      default:
        if (actionId) {
          this.attachCustomPolygonMenuActionHandler(button, actionId, latLngs, featureGroup);
        }
    }
  }

  private startPolygonTransform(featureGroup: L.FeatureGroup, mode: PolygonTransformMode): void {
    const existing = this.transformControllers.get(featureGroup);
    if (existing) {
      existing.cancel();
      existing.destroy();
      this.transformControllers.delete(featureGroup);
    }

    if (mode === 'donut' && this.donutTargetHasHoles(featureGroup)) {
      return;
    }

    try {
      const controller = new PolygonTransformController(
        this.map,
        featureGroup,
        mode,
        this.config.polygonTools.donut.direction,
        (confirmed) =>
          this.handleTransformControllerResult(controller, featureGroup, mode, confirmed),
      );
      this.transformControllers.set(featureGroup, controller);
      this.setTransformAuxiliaryVisibility(featureGroup, false);
      this.transformModeActive = true;
    } catch {
      // ignore
    }
  }

  private handleTransformControllerResult(
    controller: PolygonTransformController,
    featureGroup: L.FeatureGroup,
    mode: PolygonTransformMode,
    confirmed: boolean,
  ): void {
    if (this.transformControllers.get(featureGroup) === controller) {
      this.transformControllers.delete(featureGroup);
    }
    this.transformModeActive = false;

    const polygonLayer = this.getPolygonLayer(featureGroup);
    if (!polygonLayer) {
      return;
    }

    if (!confirmed) {
      this.setTransformAuxiliaryVisibility(featureGroup, true);
      return;
    }

    const { level: optimizationLevel, original: originalOptimizationLevel } =
      this.getOptimizationMetadataFromFeatureGroup(featureGroup);

    if (mode === 'donut') {
      this.handleDonutCommit(
        controller,
        featureGroup,
        polygonLayer,
        optimizationLevel,
        originalOptimizationLevel,
      );
      return;
    }

    if (this.saveHistoryState) {
      this.saveHistoryState(mode);
    }
    const newGeoJSON = polygonLayer.toGeoJSON();
    const featureMetadataState = this.getFeatureMetadataState(featureGroup);
    this.cleanupFeatureGroup(featureGroup);
    this.removeFeatureGroup(featureGroup);
    this.emitPolygonUpdated({
      operation: 'transform',
      polygon: this.turfHelper.getTurfPolygon(newGeoJSON),
      allowMerge: true,
      optimizationLevel,
      originalOptimizationLevel,
      featureId: featureMetadataState.featureId,
      featureMetadata: featureMetadataState.metadata,
      sourceFeatureIds: featureMetadataState.sourceFeatureIds,
      featureInteractionOverride: featureMetadataState.interactionOverride,
      featureStyleOverrides: featureMetadataState.styleOverrides,
      featureCreatedAt: featureMetadataState.createdAt,
    });
  }

  private getPolygonLayer(featureGroup: L.FeatureGroup): L.Polygon | undefined {
    return featureGroup.getLayers().find((layer): layer is L.Polygon => layer instanceof L.Polygon);
  }

  private attachMenuButtonHandlers(
    buttons: HTMLDivElement[],
    latLngs: L.LatLngLiteral[],
    featureGroup: L.FeatureGroup,
  ): void {
    buttons.forEach((button) => this.attachMenuButtonHandler(button, latLngs, featureGroup));
  }

  private enableMenuButtonPointerEvents(outerWrapper: HTMLElement): void {
    outerWrapper.querySelectorAll('.marker-menu-button').forEach((btn) => {
      (btn as HTMLElement).style.pointerEvents = 'auto';
      btn.addEventListener('click', (event) => event.stopPropagation());
    });
  }

  private generateMenuMarkerPopup(
    latLngs: L.LatLngLiteral[],
    featureGroup: L.FeatureGroup,
  ): L.Popup {
    const buttons = [
      ...this.getBuiltInMenuButtons(featureGroup),
      ...this.getPolygonMenuActionButtons(featureGroup),
    ];

    const outerWrapper = PopupFactory.buildMenuPopup(buttons);
    this.attachMenuPopupCloseHandler(outerWrapper);
    this.attachMenuButtonHandlers(buttons, latLngs, featureGroup);

    L.DomEvent.disableClickPropagation(outerWrapper);
    outerWrapper.style.pointerEvents = 'auto';
    this.enableMenuButtonPointerEvents(outerWrapper);

    const isMobile = globalThis.innerWidth <= 600;
    const versionClass = LeafletVersionDetector.isV1() ? ' leaflet-v1' : ' leaflet-v2';
    const popup = leafletAdapter
      .createPopup({
        closeButton: false,
        autoClose: true,
        className: `menu-popup${isMobile ? ' mobile-popup' : ''}${versionClass}`,
      })
      .setContent(outerWrapper);

    this._openMenuPopup = popup;
    return popup;
  }

  private getMenuActionContext(featureGroup: L.FeatureGroup): PolygonMenuActionContext {
    const completePolygon = this.getPolygonGeoJSONFromFeatureGroup(featureGroup);
    const polygonLayer = featureGroup.getLayers().find((layer) => layer instanceof L.Polygon) as
      | L.Polygon
      | undefined;

    return {
      polygon: this.turfHelper.getTurfPolygon(completePolygon),
      featureGroup,
      bounds: polygonLayer ? polygonLayer.getBounds() : leafletAdapter.createLatLngBounds(),
    };
  }

  private toggleOptimizationVisibility(featureGroup: L.FeatureGroup): void {
    const polygonLayer = featureGroup.getLayers().find((l) => l instanceof L.Polygon) as
      | L.Polygon
      | undefined;
    if (!polygonLayer) {
      return;
    }
    const metadata = this.getOptimizationMetadataFromFeatureGroup(featureGroup);
    if (!metadata.original && !metadata.level) {
      return;
    }
    const targetLevel =
      metadata.level > 0 ? 0 : metadata.original > 0 ? metadata.original : metadata.level;
    if (targetLevel === metadata.level) {
      return;
    }
    const newGeoJSON = polygonLayer.toGeoJSON();
    const featureMetadataState = this.getFeatureMetadataState(featureGroup);
    this.cleanupFeatureGroup(featureGroup);
    this.removeFeatureGroup(featureGroup);
    this.emitPolygonUpdated({
      operation: 'toggleOptimization',
      polygon: this.turfHelper.getTurfPolygon(newGeoJSON),
      allowMerge: true,
      optimizationLevel: targetLevel,
      originalOptimizationLevel: metadata.original || targetLevel,
      featureId: featureMetadataState.featureId,
      featureMetadata: featureMetadataState.metadata,
      sourceFeatureIds: featureMetadataState.sourceFeatureIds,
      featureInteractionOverride: featureMetadataState.interactionOverride,
      featureStyleOverrides: featureMetadataState.styleOverrides,
      featureCreatedAt: featureMetadataState.createdAt,
    });
  }

  private getPolygonGeoJSONFromFeatureGroup(
    featureGroup: L.FeatureGroup,
  ): Feature<Polygon | MultiPolygon> {
    try {
      // Find the polygon layer in the feature group (use getLayers + find for clearer typing)
      const polygonLayer = featureGroup.getLayers().find((layer) => layer instanceof L.Polygon) as
        | L.Polygon
        | undefined;

      if (!polygonLayer) {
        throw new Error('No polygon found in feature group');
      }

      // Get the complete GeoJSON including holes
      return polygonLayer.toGeoJSON() as Feature<Polygon | MultiPolygon>;
    } catch (error) {
      if (!isTestEnvironment()) {
        if (error instanceof Error) {
          console.warn('Error getting polygon GeoJSON from feature group:', error.message);
        } else {
          console.warn('Error getting polygon GeoJSON from feature group:', error);
        }
      }
      // Fallback: return a simple polygon
      return {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [0, 0],
              [0, 1],
              [1, 1],
              [1, 0],
              [0, 0],
            ],
          ],
        },
        properties: {},
      };
    }
  }

  private getTotalPolygonPerimeter(polygonGeoJSON: Feature<Polygon | MultiPolygon>): number {
    try {
      if (!polygonGeoJSON || !polygonGeoJSON.geometry) {
        return 0;
      }

      let totalPerimeter = 0;

      if (polygonGeoJSON.geometry.type === 'Polygon') {
        // For a single polygon, sum all ring perimeters
        const coordinates = polygonGeoJSON.geometry.coordinates;

        for (const ring of coordinates) {
          // Create a temporary polygon for each ring to calculate its perimeter
          const ringPolygon: Feature<Polygon> = {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [ring],
            },
            properties: {},
          };

          const ringPerimeter = this.turfHelper.getPolygonPerimeter(ringPolygon);
          totalPerimeter += ringPerimeter;
        }
      } else if (polygonGeoJSON.geometry.type === 'MultiPolygon') {
        // For multipolygon, sum all polygons' total perimeters
        const coordinates = polygonGeoJSON.geometry.coordinates;

        for (const polygonCoords of coordinates) {
          for (const ring of polygonCoords) {
            const ringPolygon: Feature<Polygon> = {
              type: 'Feature',
              geometry: {
                type: 'Polygon',
                coordinates: [ring],
              },
              properties: {},
            };

            const ringPerimeter = this.turfHelper.getPolygonPerimeter(ringPolygon);
            totalPerimeter += ringPerimeter;
          }
        }
      }

      // Convert from kilometers to meters to match original behavior
      return totalPerimeter * 1000;
    } catch (error) {
      if (!isTestEnvironment()) {
        if (error instanceof Error) {
          console.warn('Error calculating total polygon perimeter:', error.message);
        } else {
          console.warn('Error calculating total polygon perimeter:', error);
        }
      }
      // Fallback to turf helper calculation
      return this.turfHelper.getPolygonPerimeter(polygonGeoJSON) * 1000;
    }
  }

  private generateInfoMarkerPopup(area: number, perimeter: number): L.Popup {
    type PerimeterConfig = ConstructorParameters<typeof Perimeter>[1];
    type AreaConfig = ConstructorParameters<typeof Area>[1];
    const _perimeter = new Perimeter(perimeter, this.config as unknown as PerimeterConfig);
    const _area = new Area(area, this.config as unknown as AreaConfig);
    const infoConfig = this.config.markers.markerInfoIcon ?? {};
    const showArea = infoConfig.showArea !== false;
    const showPerimeter = infoConfig.showPerimeter !== false;
    const useMetricUnits = infoConfig.useMetrics !== false;
    const areaLabel = infoConfig.areaLabel?.trim();
    const perimeterLabel = infoConfig.perimeterLabel?.trim();

    const outerWrapper: HTMLDivElement = document.createElement('div');
    outerWrapper.classList.add('info-marker-outer-wrapper');
    const wrapper: HTMLDivElement = document.createElement('div');
    wrapper.classList.add('info-marker-wrapper');
    const markerContent: HTMLDivElement = document.createElement('div');
    markerContent.classList.add('content');

    const infoContentWrapper: HTMLDivElement = document.createElement('div');
    infoContentWrapper.classList.add('info-marker-content');

    if (showArea) {
      const areaValue = useMetricUnits
        ? `${_area.metricArea} ${_area.metricUnit}`
        : `${_area.imperialArea} ${_area.imperialUnit}`;
      const areaDiv: HTMLDivElement = document.createElement('div');
      areaDiv.classList.add('info-item', 'area');
      areaDiv.innerHTML =
        areaLabel && areaLabel.length > 0
          ? `<strong>${areaLabel}:</strong> ${areaValue}`
          : areaValue;
      infoContentWrapper.appendChild(areaDiv);
    }

    if (showPerimeter) {
      const perimeterValue = useMetricUnits
        ? `${_perimeter.metricLength} ${_perimeter.metricUnit}`
        : `${_perimeter.imperialLength} ${_perimeter.imperialUnit}`;
      const perimeterDiv: HTMLDivElement = document.createElement('div');
      perimeterDiv.classList.add('info-item', 'perimeter');
      perimeterDiv.innerHTML =
        perimeterLabel && perimeterLabel.length > 0
          ? `<strong>${perimeterLabel}:</strong> ${perimeterValue}`
          : perimeterValue;
      infoContentWrapper.appendChild(perimeterDiv);
    }

    markerContent.appendChild(infoContentWrapper);

    outerWrapper.appendChild(wrapper);
    wrapper.appendChild(markerContent);

    L.DomEvent.disableClickPropagation(outerWrapper);
    outerWrapper.style.pointerEvents = 'auto';

    outerWrapper.querySelectorAll('button').forEach((btn) => {
      (btn as HTMLElement).style.pointerEvents = 'auto';
      btn.addEventListener('click', (e) => e.stopPropagation());
    });

    const isMobile = window.innerWidth <= 600;
    const popup = leafletAdapter
      .createPopup({
        closeButton: true,
        autoClose: true,
        className: `info-popup${isMobile ? ' mobile-popup' : ''}`,
      })
      .setContent(outerWrapper);

    return popup;
  }
}
